import React, { useState, useEffect, useMemo, useRef } from 'react';
import { format, parseISO } from 'date-fns';
import { statusTint } from '../../lib/theme';
import { FormField, DatePicker, TimePicker, NumberField, ChoiceKeys, Combobox, Switch, Button, FieldError, FormErrorBanner, MoveHeaderCard, AutosaveStatus } from '../ui';
import { useAutosave } from '../../hooks/useAutosave';
// Moves (2026-09-18, moves-architecture.md): this tab prices the client's
// CURRENT MOVE. Dates / addresses / job type come from the move (read-only
// here — edit them on the Details tab); the estimate it creates is an
// OPTION on that move (db.createOption fills the identity columns).
import { currentMoveFor, optionsForMove, deriveTripPhases, fmtMoveDate } from '../../lib/moves';
import { IconUsers, IconDollarSign, IconFileText, IconTruck, IconPlus, IconTrash } from '../../components/icons';
import { useAuth } from '../../auth/AuthContext';
import { useStore } from '../../store/StoreContext';
import { useToast } from '../../hooks/useToast';
import { lookupRate, lookupPackingRate, getEstimateStatus } from '../../utils/';
import { getFurnitureClassOptions, TRUCK_TYPE_OPTIONS, TRUCK_EXTRA_SIZES, truckSizeShortLabel } from '../../lib/constants';
import PhasePlannerInline, { derivePhases } from './PhasePlannerInline';
import ConfirmDialog from '../ui/ConfirmDialog';
import { useConfirm } from '../../hooks/useConfirm';
import { buildOccupancy, unitState, unitRate, byId, tenancyAddress, tenancyPlace, STORAGE_SIZES } from '../../lib/storageHelpers';
import db, { PHASE_KINDS, makePhase, normalizePhases } from '../../lib/database';
import { kindSegments, kindId, kindShortLabel, kindIsPack, kindIsPackOnly, phaseLaborPricing, placePhasePricing, phasePackPricing } from '../../lib/phaseKinds';

// Furniture-class subtitles — informational descriptions of each tier.
// Kept here (not in app_config) because they're content, not configurable
// colors. Keyed by the canonical tier identifier so even if a user renames
// a tier label, the description still resolves.
const FURNITURE_SUBTITLES = {
    good:    'Entry-level / budget — composite wood, plastic, PVC tubes. Designed for quick assembly.',
    better:  'Mid-range / value — improved durability, better upholstery, varied finishes.',
    best:    'High-end / heirloom — solid wood, high-quality fabric, attention to detail.',
    luxury:  'Bespoke / prestige — custom-made with premium materials and superior craftsmanship.',
};

const inputCls = 'w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-red-500/20 focus:border-red-400 placeholder:text-slate-400';

// Packing crew keys (1–4). Values are numbers so `parseInt(v)` downstream
// keeps packingCrewSize / day.crew numeric, same as the old native dropdown.
const PACKING_CREW_ITEMS = [1, 2, 3, 4].map(n => ({ value: n, label: `${n} ${n === 1 ? 'packer' : 'packers'}` }));

// ── BOL deposit provenance (sql/092) ─────────────────────────────────────
// A deposit on an out-of-state BOL is either still REQUESTED from the
// client or COLLECTED by someone (initials + how). Mirrors the items and
// helpers in EstimateExpandedDetails so both cards read identically.
const BOL_DEPOSIT_STATUS_ITEMS = [
    { value: 'requested', label: 'Requested', color: '#d97706' },
    { value: 'collected', label: 'Collected', color: '#059669' },
];
const BOL_DEPOSIT_METHOD_ITEMS = [
    { value: 'cash', label: 'Cash' }, { value: 'card', label: 'Card' }, { value: 'check', label: 'Check' },
    { value: 'zelle', label: 'Zelle' }, { value: 'other', label: 'Other' },
];
// Logged-in user's initials — display name from auth metadata when set,
// else the email local-part ("jaylen" → "JA", "jaylen.beaty" → "JB").
function userInitials(user) {
    const meta = user?.user_metadata || {};
    const name = meta.full_name || meta.display_name || meta.name || (user?.email || '').split('@')[0] || '';
    const parts = String(name).trim().split(/[\s._-]+/).filter(Boolean);
    if (parts.length === 0) return '';
    const init = parts.length === 1 ? parts[0].slice(0, 2) : parts.map(p => p[0]).join('').slice(0, 3);
    return init.toUpperCase();
}

// TRUCK_TYPE_OPTIONS, TRUCK_EXTRA_SIZES, truckSizeShortLabel are now
// shared via src/lib/constants.js so the OOS estimate creation can use
// the same picker (May 2026 unification — see sales-lead requests).

// editEstimate (2026-09-19, estimates-page.md pick 2A): when an existing
// option is passed, this tab becomes that option's AUTOSAVING editor — same
// keys, same math — and the create-only pieces (storage linkage, deposit
// transfer, draft switch, Create button, previous-estimates list) hide.
export default function ClientPricingTab({ client, form, set, config, trucks, readOnly, saveEstimate, estimates, onEstimateCreated, initialFrom = null, editEstimate = null, autosaveRef = null }) {
    const isEdit = !!editEstimate?.id;
    // The whole tab is about pricing — splitting view vs edit gives us
    // a future seam for view-only pricing access. Today's tiers grant
    // both together for admins, neither for office.
    const { can, user } = useAuth();
    const canViewPricing = can('view_estimate_pricing');
    const canEditPricing = can('edit_pricing');
    const { saveClient, storageFacilities, storageUnits, storageTenancies, saveStorageTenancy, patchStorageTenancy, clients: allClients, moves, saveMove, refreshMove } = useStore();
    const { show: showToast } = useToast();
    // The move being priced. `client` may be a snapshot — read the live row
    // so a switcher / booking flip of current_move_id lands here.
    const liveClient = (allClients || []).find(c => c.id === client?.id) || client;
    const current = currentMoveFor(liveClient, moves);
    const moveOptions = useMemo(() => optionsForMove(estimates, current?.id), [estimates, current?.id]);
    const [creating, setCreating] = useState(false);
    // 4B: a refused create lists its problems in a banner at the top of the form.
    const [formErrors, setFormErrors] = useState([]);

    // Furniture class options resolve from app_config so Settings →
    // StatusManager color edits propagate into this picker. Priority is
    // NOT in this form (per Jaylen, 2026-05-14): priority lives on the
    // client and is set independently from any surface — Clients page,
    // Estimates page, EstimateExpandedDetails — by admins/owners.
    const FURNITURE_CLASS_OPTIONS = useMemo(() => getFurnitureClassOptions(config), [config?.furniture_class_options]);

    // Lead-pipeline fields (sql/047). Priority was moved off this form
    // in sql/049 — it's now a client-level concept settable from any
    // admin surface (Clients page, Estimates page, EstimateExpandedDetails).
    // Furniture class lives on the client and persists immediately on
    // change. Draft is the rare opt-out from the callback queue.
    const [isDraft, setIsDraft] = useState(false);
    const furnitureClass = form.furniture_class || client?.furniture_class || 'good';
    const updateFurnitureClass = async (newClass) => {
        if (newClass === furnitureClass) return;
        set('furniture_class', newClass);
        if (client?.id) {
            try {
                await saveClient({ ...client, furniture_class: newClass });
            } catch (e) {
                showToast('Failed to save furniture class: ' + (e.message || 'Unknown error'), 'error');
            }
        }
    };

    // (The pre-create "is the move info the same?" dialog + its three
    // inline overrides are gone, 2026-09-18: options never carry dates or
    // addresses — those are the move's. "Alternate pricing" = another
    // option on the same move; a different job = "+ New move".)
    const hasPriorEstimates = moveOptions.length > 0;
    // Soft guard: OOS estimate about to save with no load/travel crew —
    // crew/truck preallocation is the point of early OOS estimates (QA M2
    // BUG-9). Holds the pending opts while the dialog is up.
    const [zeroCrewConfirm, setZeroCrewConfirm] = useState(null);
    // Promise-based in-app confirm (QA M2 NEW-9 sweep — replaces the
    // last window.confirm calls on this tab, incl. the known-open
    // no-move-date one).
    const { confirm, confirmElement } = useConfirm();
    // Deposit transfer at create time (2026-06-09, Jaylen): when a prior
    // estimate holds a PAID deposit (the "destination changed after they
    // paid" case), super-admin+ can carry the deposit + signed terms onto
    // the estimate being created. Source = newest prior estimate with a
    // paid deposit (cancelled ones included — the dead quote often holds
    // the money). Lives above the Create button now (the dialog it sat
    // in is gone).
    const [transferDeposit, setTransferDeposit] = useState(false);
    const depositSource = useMemo(() => (estimates || [])
        .filter(e => e.deposit_paid_at)
        .sort((a, b) => (b.created_at || '').localeCompare(a.created_at || ''))[0] || null,
    [estimates]);
    const canTransferDeposits = can('transfer_deposits');

    // Job type: LOCAL or LONG_DISTANCE
    // Pricing MODE (local hourly vs OOS/BOL). Seeds from the move's job
    // type; on create, a mismatch is written back to the move (job type is
    // move identity — rule 1).
    const [jobType, setJobType] = useState((current?.job_type || form.job_type) === 'LONG_DISTANCE' ? 'LONG_DISTANCE' : 'LOCAL');

    // LOCAL fields
    const [crewSize, setCrewSize] = useState(form.crew_size_needed || 0);
    const [truckType, setTruckType] = useState('26ft');
    // array of '16ft' / '20ft' / '26ft' — any combination is allowed.
    // (20ft added May 2026 per sales-lead spec; OOS now shares this picker.)
    const [extraTrucks, setExtraTrucks] = useState([]);
    // Waive the per-hour extra-truck surcharge on this estimate even
    // though the extra truck(s) are still required (dispatcher still
    // allocates them). Used for comp / repeat-customer cases — see
    // sql/038_extra_truck_fee_waived.sql.
    const [extraTruckFeeWaived, setExtraTruckFeeWaived] = useState(false);
    // How the truck reads on the WRITTEN ESTIMATE (Jaylen 2026-09-21):
    // blank = plain default ("with truck" / "with 2 trucks"); typed text
    // replaces it verbatim on the client document. The allocation itself
    // (truckType / extraTrucks) still drives dispatch and the surcharge.
    const [truckDisplay, setTruckDisplay] = useState('');
    const [hourlyRate, setHourlyRate] = useState(0);
    const [minHours, setMinHours] = useState('');
    const [maxHours, setMaxHours] = useState('');
    const [travelFee, setTravelFee] = useState(0);
    const [materialsFee, setMaterialsFee] = useState(0);
    // Hold fee (Jaylen 2026-09-11, sql/088): flat charge for keeping the
    // loaded truck between days (load / hold overnight / unload). LOCAL
    // only, priced like travel — a fixed add-on, not hourly.
    const [holdFee, setHoldFee] = useState(0);

    // Extra truck surcharge from config
    const extraTruckFeePerHr = useMemo(() => {
        try { return parseFloat(JSON.parse(config.extra_truck_fee || '25')); } catch { return 25; }
    }, [config]);

    // Packing line (both job types). Seed ON when the client file already says
    // partial/full packing (set at intake) so the estimator doesn't re-toggle it.
    // A SECOND estimate for the same client starts WITHOUT packing — the
    // intake flag already produced a pack phase on the first one, so re-seeding
    // it silently re-added a packing day (Jensen 2026-09-15, Carolyn Franz
    // second estimate). "+ Add Packing" still adds it on purpose.
    const [packingNeeded, setPackingNeeded] = useState(
        !hasPriorEstimates && ((current?.packing_needed || form.packing_needed) === 'partial' || (current?.packing_needed || form.packing_needed) === 'full')
    );
    const [packingCrewSize, setPackingCrewSize] = useState(2);
    const [packingRate, setPackingRate] = useState(0);
    const [packingMinHours, setPackingMinHours] = useState('');
    const [packingMaxHours, setPackingMaxHours] = useState('');
    // Packing DAY (+ optional time) — the MOVE's pack date (2026-09-18):
    // read-only here, edited on the Details tab's trip strip. Siblings
    // share it (rule 2 — options never carry dates).
    const shiftDateStr = (iso, days) => {
        if (!iso) return null;
        const [y, m, d] = String(iso).split('-').map(Number);
        if (!y) return null;
        const dt = new Date(Date.UTC(y, m - 1, d));
        dt.setUTCDate(dt.getUTCDate() + days);
        return dt.toISOString().slice(0, 10);
    };
    const packDate = current?.pack_date || '';
    const packTime = current?.pack_time || '';
    const moveDate = current?.load_date || null;

    // ── Multi-day packing (Jensen 2026-08-25) ────────────────────────────
    // Long-distance (and big local) moves can need a 2nd/3rd packing day.
    // Day 1 lives in the legacy single-day fields above; each EXTRA day is
    // { date, time, crew, rate, minHours, maxHours } here. Every day becomes
    // its own PACK phase (own date/time/crew + per-day hours & rate), so it
    // lands on the dispatch board as its own pill with its own estimate.
    // The estimate's packing_min/max_hours columns become the SUM across
    // days so the existing pricing math + display surfaces keep working.
    const [extraPackDays, setExtraPackDays] = useState([]);
    const addPackDay = () => {
        setExtraPackDays(days => {
            const lastDate = days.length > 0 ? days[days.length - 1].date : packDate;
            const crew = packingCrewSize;
            return [...days, {
                date: lastDate ? shiftDateStr(lastDate, 1) : '',
                time: packTime || '',
                crew,
                rate: lookupPackingRate(config, crew) || packingRate || 0,
                minHours: '',
                maxHours: '',
            }];
        });
    };
    const updatePackDay = (idx, field, value) => {
        setExtraPackDays(days => days.map((d, i) => {
            if (i !== idx) return d;
            const next = { ...d, [field]: value };
            // Crew change re-auto-fills that day's rate (still editable).
            if (field === 'crew') {
                const r = lookupPackingRate(config, Number(value) || 0);
                if (r > 0) next.rate = r;
            }
            return next;
        }));
    };
    const removePackDay = (idx) => setExtraPackDays(days => days.filter((_, i) => i !== idx));
    // Stable key so effects can watch the array by value, not identity.
    const extraPackDaysKey = JSON.stringify(extraPackDays);

    // ── Storage (sql/084-086, Jaylen 2026-08-27 → 09-08) ────────────────
    // The storage flow STARTS at estimate creation: this move can be INTO
    // storage (creates a QUOTED tenancy — nothing is physically stored
    // until the move-in completes; the estimates trigger flips it active)
    // or OUT of an existing tenancy (links this estimate as the move-out).
    // v2: "BBM unit" = pick a FACILITY (default Extra Space) and one-or-
    // more EMPTY rooms from the inventory (or assign later); the facility
    // address becomes the destination. "Client's own" stays free text.
    const [storageMode, setStorageMode] = useState('none'); // none | into | out
    const [storageOwnership, setStorageOwnership] = useState('bbm'); // bbm | client
    const [storageFacilityId, setStorageFacilityId] = useState(null);
    const [storagePickedUnitIds, setStoragePickedUnitIds] = useState([]);
    const [storageFacility, setStorageFacility] = useState('');       // client-own: free text
    const [storageUnitNumber, setStorageUnitNumber] = useState('');   // client-own: free text
    const [storageOutTenancyId, setStorageOutTenancyId] = useState(null);
    const activeFacilities = useMemo(() => (storageFacilities || []).filter(f => f.active !== false), [storageFacilities]);
    const defaultFacility = useMemo(() => activeFacilities.find(f => f.is_default) || activeFacilities[0] || null, [activeFacilities]);
    useEffect(() => { if (!storageFacilityId && defaultFacility) setStorageFacilityId(defaultFacility.id); }, [defaultFacility, storageFacilityId]);
    const storageFacilityRow = useMemo(() => activeFacilities.find(f => f.id === storageFacilityId) || null, [activeFacilities, storageFacilityId]);
    const storageUnitsById = useMemo(() => byId(storageUnits), [storageUnits]);
    const storageFacilitiesById = useMemo(() => byId(storageFacilities), [storageFacilities]);
    const storageOccupancy = useMemo(() => buildOccupancy(storageTenancies), [storageTenancies]);
    // Empty rental rooms at the chosen facility, grouped by size.
    const emptyUnitsBySize = useMemo(() => {
        const groups = {};
        (storageUnits || [])
            .filter(u => u.facility_id === storageFacilityId && unitState(u, storageOccupancy) === 'empty')
            .forEach(u => { (groups[u.size || 'other'] ||= []).push(u); });
        const order = (a, b) => { const ia = STORAGE_SIZES.indexOf(a), ib = STORAGE_SIZES.indexOf(b); return (ia < 0 ? 99 : ia) - (ib < 0 ? 99 : ib) || a.localeCompare(b); };
        return Object.keys(groups).sort(order).map(size => ({ size, units: groups[size] }));
    }, [storageUnits, storageFacilityId, storageOccupancy]);
    const togglePickedUnit = (id) => setStoragePickedUnitIds(ids => ids.includes(id) ? ids.filter(x => x !== id) : [...ids, id]);
    const pickedUnitsRate = storagePickedUnitIds.reduce((s, id) => s + unitRate(storageUnitsById[id], storageFacilityRow), 0);
    const intoDestPreview = storageOwnership === 'bbm' ? (storageFacilityRow?.address || storageFacilityRow?.name || '') : storageFacility.trim();
    // Client's existing not-closed tenancies — the "out of storage" pick list.
    const clientStorageTenancies = useMemo(
        () => (storageTenancies || []).filter(t => t.client_id === client?.id && t.status !== 'closed'),
        [storageTenancies, client?.id]
    );

    // Heavy items
    const [heavyItemCount, setHeavyItemCount] = useState(0);
    const heavyItemFee = useMemo(() => {
        try { return parseFloat(JSON.parse(config.heavy_item_fee || '200')); } catch { return 200; }
    }, [config]);

    // Removal fee
    const [removalFee, setRemovalFee] = useState(0);

    // LONG DISTANCE (BOL) fields
    const [bolPrice, setBolPrice] = useState('');
    const [bolReference, setBolReference] = useState('');
    // Estimated shipment weight (lbs) — brothers fill this during
    // walkthrough review; it feeds the manual state-tariff lookup that
    // produces bolPrice LATER (often days later, while status=building).
    const [estimatedWeight, setEstimatedWeight] = useState('');
    // BOL-specific crew split:
    //   - loadCrewSize = people on site for the wrap/load (reuses the same
    //     crew_size_needed column as local hourly moves)
    //   - travelCrewSize = subset of the load crew that actually drives and
    //     unloads at the destination
    //   - bolTruckType = truck used for the trip (we still need to allocate it)
    const [loadCrewSize, setLoadCrewSize] = useState(form.crew_size_needed || 0);
    const [travelCrewSize, setTravelCrewSize] = useState(form.travel_crew_size || 0);
    const [bolTruckType, setBolTruckType] = useState(form.trucks_needed || '26ft');
    // Same shape as the LOCAL extraTrucks state (array of '16ft' / '20ft'
    // / '26ft'). Persisted to estimates.extra_trucks the same way so the
    // dispatch board treats OOS multi-truck jobs identically. (May 2026
    // unification request from the sales lead — used to be no extras at
    // all on OOS, forcing dispatch to manually add a second truck on the
    // board.) Hydrates from form.extra_trucks for edit flows.
    const [bolExtraTrucks, setBolExtraTrucks] = useState(() => {
        try { return JSON.parse(form.extra_trucks || '[]') || []; } catch { return []; }
    });
    // Pre-paid deposit collected at BOL sign-time (by the tariff carrier,
    // often before BBM ever sees the job). Subtracted from the tariff total
    // so BBM's estimate reflects the outstanding balance.
    const [bolDeposit, setBolDeposit] = useState('');
    // Provenance (sql/092): a new estimate's deposit starts as REQUESTED;
    // "Mark collected" stamps who/how/when. Saved with the amount.
    const [bolDepositStatus, setBolDepositStatus] = useState('requested');
    const [bolDepositCollectedBy, setBolDepositCollectedBy] = useState('');
    const [bolDepositMethod, setBolDepositMethod] = useState(null);
    const [bolDepositCollectedAt, setBolDepositCollectedAt] = useState(null);
    const markBolCollected = () => {
        setBolDepositStatus('collected');
        setBolDepositCollectedAt(prev => prev || new Date().toISOString());
        setBolDepositCollectedBy(prev => prev || userInitials(user));
    };
    const bolDepositSuffix = bolDepositStatus === 'collected'
        ? ` · collected${bolDepositCollectedBy ? ` by ${bolDepositCollectedBy}` : ''}${bolDepositMethod ? ` (${bolDepositMethod})` : ''}`
        : ' · requested';

    // ------------------------------------------------------------------------
    // PHASES — local planner state that will land on the new estimate as
    // planned_phases. Auto-derived from the pricing inputs above (job type,
    // packing, crew) so the planner reflects the user's decisions without
    // them having to duplicate-enter anything. Once a user manually touches
    // the planner (adds/removes/edits a phase), `phasesDirty` flips and we
    // stop overwriting their work on every keystroke.
    // ------------------------------------------------------------------------
    const [phases, setPhases] = useState(() => derivePhases({
        jobType,
        packingNeeded: packingNeeded ? 'yes' : 'none',
        crewSize,
        loadCrewSize,
        travelCrewSize,
        packingCrewSize,
        truckType,
        bolTruckType,
        moveDate: moveDate,
        packDate: packDate || null,
        packTime: packTime || null,
        packingMinHours, packingMaxHours, packingRate,
        extraPackDays,
    }));
    const [phasesDirty, setPhasesDirty] = useState(false);

    // Re-derive phases whenever the inputs that control their SHAPE change
    // (job type, packing toggle), but only while the planner is still in
    // "auto" mode. Once the user has touched the planner (adds/removes/
    // reorders phases), `phasesDirty` flips and we stop overwriting.
    useEffect(() => {
        if (phasesDirty) return;
        setPhases(derivePhases({
            jobType,
            packingNeeded: packingNeeded ? 'yes' : 'none',
            crewSize,
            loadCrewSize,
            travelCrewSize,
            packingCrewSize,
            truckType,
            bolTruckType,
            moveDate: moveDate,
            packDate: packDate || null,
            packTime: packTime || null,
            packingMinHours, packingMaxHours, packingRate,
            extraPackDays,
        }));
    }, [
        phasesDirty,
        jobType,
        packingNeeded,
        crewSize,
        loadCrewSize,
        travelCrewSize,
        packingCrewSize,
        truckType,
        bolTruckType,
        moveDate,
        packDate,
        packTime,
        packingMinHours,
        packingMaxHours,
        packingRate,
        extraPackDaysKey,
    ]);

    // Multi-day pricing (Jaylen 2026-06-09): LOCAL load-hold-unload moves
    // price each working day separately — its own crew, hours range, and
    // rate (auto from the rate table for that day's crew). Lives in the
    // phases array (est_min_hours / est_max_hours / hourly_rate per
    // phase) so the breakdown persists on planned_phases and flows to
    // the written-estimate export.
    const [multiDay, setMultiDay] = useState(false);

    // ── Alternate pricing prefill (2026-09-18) ───────────────────────────
    // The Estimates tab hands us the option to start from (`initialFrom`
    // = { est, nonce }); copy its PRICING fields only — hours, rates, crew,
    // trucks, packing, fees, planned phases (with fresh dispatch links) —
    // never its dates / addresses (the move's). Re-applies on every click
    // (nonce), so "Alternate pricing" always starts from that option.
    const [prefillNote, setPrefillNote] = useState('');
    // Seed every pricing field from an option row. `freshLinks` drops the
    // planner's dispatch links (Alternate pricing — the new option isn't on
    // the board); the edit seeder keeps them (this option may be).
    const seedFromEstimate = (est, { freshLinks }) => {
        try {
            const isBOL = est.job_type === 'LONG_DISTANCE';
            setJobType(isBOL ? 'LONG_DISTANCE' : 'LOCAL');
            const extras = (() => { try { const x = JSON.parse(est.extra_trucks || '[]'); return Array.isArray(x) ? x : []; } catch { return []; } })();
            if (isBOL) {
                setLoadCrewSize(Number(est.crew_size_needed) || 0);
                setTravelCrewSize(Number(est.travel_crew_size) || 0);
                setBolTruckType(est.trucks_needed || '26ft');
                setBolExtraTrucks(extras);
                setBolPrice(est.bol_price ? String(est.bol_price) : '');
                setBolReference(est.bol_reference || '');
                setEstimatedWeight(est.estimated_weight_lbs ? String(est.estimated_weight_lbs) : '');
                setBolDeposit(est.bol_deposit ? String(est.bol_deposit) : '');
                setBolDepositStatus(est.bol_deposit_status || 'requested');
                setBolDepositCollectedBy(est.bol_deposit_collected_by || '');
                setBolDepositMethod(est.bol_deposit_method || null);
                setBolDepositCollectedAt(est.bol_deposit_collected_at || null);
            } else {
                setCrewSize(Number(est.crew_size_needed) || 0);
                setTruckType(est.trucks_needed || '26ft');
                setExtraTrucks(extras);
                setExtraTruckFeeWaived(!!est.extra_truck_fee_waived);
                setTruckDisplay(est.truck_display || '');
                setHourlyRate(Number(est.hourly_rate) || 0);
                setMinHours(est.min_hours ? String(est.min_hours) : '');
                setMaxHours(est.max_hours ? String(est.max_hours) : '');
                setTravelFee(Number(est.travel_fee) || 0);
                setMaterialsFee(Number(est.materials_fee) || 0);
                setHoldFee(Number(est.hold_fee) || 0);
            }
            const packOn = est.packing_needed === 'yes' || est.packing_needed === 'partial' || est.packing_needed === 'full' || (Number(est.packing_hours) || 0) > 0;
            setPackingNeeded(packOn);
            if (packOn) {
                setPackingCrewSize(Number(est.packing_crew_size) || 2);
                setPackingRate(Number(est.packing_rate) || 0);
                setPackingMinHours(est.packing_min_hours ? String(est.packing_min_hours) : '');
                setPackingMaxHours(est.packing_max_hours ? String(est.packing_max_hours) : '');
            }
            setExtraPackDays([]);
            setHeavyItemCount(Number(est.heavy_item_count) || 0);
            setRemovalFee(Number(est.removal_fee) || 0);
            const priced = Array.isArray(est.planned_phases) && est.planned_phases.length > 0
                && est.planned_phases.some(p => Number(p.est_min_hours) || Number(p.est_max_hours) || Number(p.hourly_rate));
            setMultiDay(!isBOL && !(Number(est.hourly_rate) || 0) && priced);
            if (Array.isArray(est.planned_phases) && est.planned_phases.length > 0) {
                setPhases(normalizePhases(freshLinks ? est.planned_phases.map(p => ({ ...p, move_job_ids: [], move_job_id: null })) : est.planned_phases));
                setPhasesDirty(true);
            } else {
                setPhasesDirty(false);
            }
        } catch (e) { console.warn('pricing seed skipped:', e?.message || e); }
    };
    useEffect(() => {
        const est = initialFrom?.est;
        if (!est) return;
        seedFromEstimate(est, { freshLinks: true });
        setPrefillNote(`Pricing copied from ${est.job_number}${est.option_letter ? `-${est.option_letter}` : ''} — same dates and addresses; change what's different and create.`);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialFrom?.nonce]);
    // Edit mode: seed from the option once per option id, then autosave
    // every change (below). seededRef stops the first fingerprint from
    // writing the row back unchanged.
    const seededIdRef = useRef(null);
    // The rate auto-fill effects fire on the seed's crew / packing changes
    // and would stomp a hand-set rate; they skip one run per seed.
    const keepSeededRatesRef = useRef({ hourly: null, packing: null });
    useEffect(() => {
        if (!isEdit) return;
        keepSeededRatesRef.current = {
            hourly: editEstimate.job_type !== 'LONG_DISTANCE' ? { crew: Number(editEstimate.crew_size_needed) || 0, truck: editEstimate.trucks_needed || '26ft' } : null,
            packing: { crew: Number(editEstimate.packing_crew_size) || 2 },
        };
        seedFromEstimate(editEstimate, { freshLinks: false });
        setIsDraft(!!editEstimate.is_draft);
        seededIdRef.current = editEstimate.id;
        lastSavedFpRef.current = null;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [editEstimate?.id]);


    // ALWAYS propagate crew sizes from the allocation area into existing
    // phases, even when phasesDirty is true. The allocation selectors are
    // the source of truth for crew — the planner chips just display them.
    // This runs on every crew/truck change regardless of dirty state.
    // EXCEPT in multi-day pricing mode, where the day table owns each
    // day's crew and the blanket sync would stomp per-day edits.
    useEffect(() => {
        if (multiDay && jobType === 'LOCAL') return;
        setPhases(prev => {
            const isBOL = jobType === 'LONG_DISTANCE';
            let changed = false;
            // Only the FIRST pack phase mirrors the day-1 packing crew
            // selector — extra packing days own their crews via the day
            // rows in the Packing card (multi-day packing, 2026-08-25).
            let packSeen = false;
            const next = prev.map(p => {
                let targetCrew = p.crew_size;
                let targetTruck = p.trucks_needed;
                // Buckets derive from the kind's SEGMENTS (phaseKinds.js,
                // 2026-08-26 combo refactor): pack-only → packing crew
                // (first pack phase only); anything with a move → main
                // crew; anything with a load (except pack combos, which
                // keep their hand-set crew) → load crew; pure travel
                // (drive/unload/return) → travel crew; hold/trash →
                // untouched.
                const segs = kindSegments(p.kind);
                const has = (k) => segs.includes(k);
                if (segs.length === 1 && segs[0] === 'pack') {
                    if (!packSeen) {
                        packSeen = true;
                        targetCrew = Number(packingCrewSize) || 0;
                    }
                } else if (has('move')) {
                    targetCrew = Number(crewSize) || 0;
                    targetTruck = truckType || '';
                } else if (has('load') && !has('pack')) {
                    // load_drive starts as a load day — inherit the (larger)
                    // load crew; dispatcher trims for the drive leg if needed.
                    targetCrew = Number(loadCrewSize) || 0;
                    targetTruck = isBOL ? (bolTruckType || '') : (truckType || '');
                } else if (has('drive') || has('unload') || has('return')) {
                    targetCrew = Number(travelCrewSize) || 0;
                    targetTruck = isBOL ? (bolTruckType || '') : '';
                }
                if (targetCrew !== p.crew_size || targetTruck !== (p.trucks_needed || '')) {
                    changed = true;
                    return { ...p, crew_size: targetCrew, trucks_needed: targetTruck };
                }
                return p;
            });
            return changed ? next : prev;
        });
    }, [jobType, crewSize, loadCrewSize, travelCrewSize, packingCrewSize, truckType, bolTruckType, phases.length, multiDay]);

    // Extra packing days must reach the phases array even when the user
    // has hand-edited the planner (phasesDirty — the wholesale re-derive
    // above is disabled then). Day 1's pack phase is left alone; every
    // OTHER pack phase is treated as generated from extraPackDays and
    // rebuilt in place. Without this, adding "Packing Day 2" after
    // touching the planner priced the day but never created its phase.
    useEffect(() => {
        if (!phasesDirty) return;
        setPhases(prev => {
            const firstPackIdx = prev.findIndex(p => p.kind === 'pack');
            if (firstPackIdx === -1) return prev; // planner has no packing — leave it be
            const withoutExtras = prev.filter((p, i) => p.kind !== 'pack' || i === firstPackIdx);
            const extras = extraPackDays.map(day => ({
                ...makePhase({ kind: 'pack', sequence: 0, crew_size: Number(day.crew) || 0, trucks_needed: '', date: day.date || null, time: day.time || null }),
                est_min_hours: parseFloat(day.minHours) || 0,
                est_max_hours: parseFloat(day.maxHours) || parseFloat(day.minHours) || 0,
                hourly_rate: parseFloat(day.rate) || 0,
            }));
            const anchor = withoutExtras.findIndex(p => p.kind === 'pack');
            const next = [...withoutExtras.slice(0, anchor + 1), ...extras, ...withoutExtras.slice(anchor + 1)];
            return normalizePhases(next);
        });
    }, [extraPackDaysKey]);

    // Day-1 packing hours/rate must ALSO reach the planner after it's been
    // hand-edited (phasesDirty — the wholesale re-derive above is off).
    // Mirrors the extra-days effect: the FIRST phase that packs gets the
    // day-1 packing pricing — into est_* on a pack-only day, into the
    // pack_* slots on a pack+move combo (2026-09-10, Alwin BBM-2609-018:
    // the merged day kept whatever packing numbers it had at merge time).
    useEffect(() => {
        if (!phasesDirty) return;
        setPhases(prev => {
            const idx = prev.findIndex(p => kindIsPack(p.kind));
            if (idx === -1) return prev;
            const p = prev[idx];
            const pack = packingNeeded
                ? { min: parseFloat(packingMinHours) || 0, max: parseFloat(packingMaxHours) || parseFloat(packingMinHours) || 0, rate: parseFloat(packingRate) || 0 }
                : { min: 0, max: 0, rate: 0 };
            const cur = phasePackPricing(p);
            if (cur.min === pack.min && cur.max === pack.max && cur.rate === pack.rate) return prev;
            const next = { ...p, ...placePhasePricing(p.kind, phaseLaborPricing(p), pack) };
            return prev.map((x, i) => (i === idx ? next : x));
        });
    }, [phasesDirty, packingNeeded, packingMinHours, packingMaxHours, packingRate]);

    // Per-day pricing mutator. Changing a day's crew re-auto-fills its
    // rate from the rate table (still editable afterwards) — same
    // behavior as the single-day rate field.
    const updateDayPricing = (sequence, field, value) => {
        setPhases(prev => prev.map(p => {
            if (p.sequence !== sequence) return p;
            const next = { ...p, [field]: value };
            if (field === 'crew_size') {
                const rate = lookupRate(config, Number(value) || 0, truckType, 'moving');
                if (rate > 0) next.hourly_rate = rate;
            }
            return next;
        }));
        setPhasesDirty(true);
    };

    const handlePhasesChange = (next) => {
        setPhases(next);
        setPhasesDirty(true);
    };

    // "Pre-fill from move dates" — shown when the planner is empty or only
    // the auto-seeded single day with no pricing on it, and the move has a
    // load date. Uses the allocation area's crew / truck for every chip.
    const canPrefillFromMove = !!current?.load_date && (
        phases.length === 0
        || (phases.length === 1 && ['move', 'load'].includes(phases[0].kind) && !(Number(phases[0].est_min_hours) || Number(phases[0].est_max_hours) || Number(phases[0].hourly_rate)))
    );
    const handlePrefillFromMove = () => {
        if (!current) return;
        const isBOL = jobType === 'LONG_DISTANCE';
        const next = deriveTripPhases(current, {
            crewSize: Number(isBOL ? loadCrewSize : crewSize) || 0,
            trucks: isBOL ? (bolTruckType || '') : (truckType || ''),
        });
        if (!next.length) return;
        setPhases(normalizePhases(next));
        setPhasesDirty(true);
    };

    const handleResetPhases = () => {
        setPhases(derivePhases({
            jobType,
            packingNeeded: packingNeeded ? 'yes' : 'none',
            crewSize,
            loadCrewSize,
            travelCrewSize,
            packingCrewSize,
            truckType,
            bolTruckType,
            moveDate: moveDate,
            packDate: packDate || null,
            packTime: packTime || null,
            packingMinHours, packingMaxHours, packingRate,
            extraPackDays,
        }));
        setPhasesDirty(false);
    };

    // Auto-fill hourly rate from rate table when crew/truck changes
    useEffect(() => {
        if (jobType !== 'LOCAL' || crewSize < 1) return;
        // An edit seed must keep the option's stored rate, not the table's.
        const k = keepSeededRatesRef.current;
        if (k.hourly && k.hourly.crew === crewSize && k.hourly.truck === truckType) { k.hourly = null; return; }
        k.hourly = null;
        const rate = lookupRate(config, crewSize, truckType, 'moving');
        if (rate > 0) setHourlyRate(rate);
    }, [crewSize, truckType, config, jobType]);

    // Auto-fill packing rate
    useEffect(() => {
        if (!packingNeeded) return;
        const k = keepSeededRatesRef.current;
        if (k.packing && k.packing.crew === packingCrewSize) { k.packing = null; return; }
        k.packing = null;
        const rate = lookupPackingRate(config, packingCrewSize);
        if (rate > 0) setPackingRate(rate);
    }, [packingNeeded, packingCrewSize, config]);

    // ===== TOTALS =====
    // When the surcharge is waived the surcharge collapses to 0, so the
    // effective rate just equals the base hourly rate. The extras list
    // itself stays intact — the truck is still required, just not billed.
    const extraTruckSurchargePerHr = extraTruckFeeWaived
        ? 0
        : extraTrucks.length * extraTruckFeePerHr;
    const effectiveRate = (parseFloat(hourlyRate) || 0) + extraTruckSurchargePerHr;

    // Day-by-day labor (multi-day LOCAL). Pack days price through the
    // Packing section and holds are non-billable, so neither gets a row.
    // The extra-truck surcharge applies to every billed hour, same
    // semantics as the single-day model.
    // (A pack+move combo IS a labor day here — its labor side lives in
    // est_*; its packing side sits in pack_* and prices through the
    // Packing section. phaseLaborPricing keeps the two apart, 2026-09-10.)
    const dayPhases = (jobType === 'LOCAL' && multiDay)
        ? phases.filter(p => !kindIsPackOnly(p.kind) && p.kind !== 'hold')
        : [];
    const dayLaborMin = dayPhases.reduce((s, p) => { const l = phaseLaborPricing(p); return s + l.min * (l.rate + extraTruckSurchargePerHr); }, 0);
    const dayLaborMax = dayPhases.reduce((s, p) => { const l = phaseLaborPricing(p); return s + l.max * (l.rate + extraTruckSurchargePerHr); }, 0);
    const dayHoursMin = dayPhases.reduce((s, p) => s + phaseLaborPricing(p).min, 0);
    const dayHoursMax = dayPhases.reduce((s, p) => s + phaseLaborPricing(p).max, 0);

    const laborMin = jobType === 'LOCAL'
        ? (multiDay ? dayLaborMin : (parseFloat(minHours) || 0) * effectiveRate)
        : 0;
    const laborMax = jobType === 'LOCAL'
        ? (multiDay ? dayLaborMax : (parseFloat(maxHours) || parseFloat(minHours) || 0) * effectiveRate)
        : 0;
    // Packing totals — day 1 (legacy fields) + every extra day, each at its
    // own rate. Hour sums land in packing_min/max_hours on the estimate.
    const extraPackMin = packingNeeded ? extraPackDays.reduce((s, d) => s + (parseFloat(d.minHours) || 0) * (parseFloat(d.rate) || 0), 0) : 0;
    const extraPackMax = packingNeeded ? extraPackDays.reduce((s, d) => s + (parseFloat(d.maxHours) || parseFloat(d.minHours) || 0) * (parseFloat(d.rate) || 0), 0) : 0;
    const extraPackMinHours = packingNeeded ? extraPackDays.reduce((s, d) => s + (parseFloat(d.minHours) || 0), 0) : 0;
    const extraPackMaxHours = packingNeeded ? extraPackDays.reduce((s, d) => s + (parseFloat(d.maxHours) || parseFloat(d.minHours) || 0), 0) : 0;
    const packingMinTotal = packingNeeded ? (parseFloat(packingMinHours) || 0) * (parseFloat(packingRate) || 0) + extraPackMin : 0;
    const packingMaxTotal = packingNeeded ? (parseFloat(packingMaxHours) || parseFloat(packingMinHours) || 0) * (parseFloat(packingRate) || 0) + extraPackMax : 0;
    const heavyTotal = heavyItemCount * heavyItemFee;
    const fixedAddOns = (parseFloat(travelFee) || 0) + (parseFloat(holdFee) || 0) + (parseFloat(materialsFee) || 0) + heavyTotal + (parseFloat(removalFee) || 0);

    // BOL deposit is only meaningful for long-distance moves; it represents
    // money already collected by the tariff carrier at sign-time, so it's
    // netted out of the total shown to the operator / customer.
    const bolDepositNum = jobType === 'LONG_DISTANCE' ? (parseFloat(bolDeposit) || 0) : 0;
    const totalMin = jobType === 'LOCAL'
        ? laborMin + packingMinTotal + fixedAddOns
        : Math.max(0, (parseFloat(bolPrice) || 0) + packingMinTotal + heavyTotal + (parseFloat(removalFee) || 0) - bolDepositNum);
    const totalMax = jobType === 'LOCAL'
        ? laborMax + packingMaxTotal + fixedAddOns
        : Math.max(0, (parseFloat(bolPrice) || 0) + packingMaxTotal + heavyTotal + (parseFloat(removalFee) || 0) - bolDepositNum);
    const isRange = totalMin !== totalMax && totalMax > 0;

    // Entry point for the Create Estimate button — straight through; the
    // move owns the dates/addresses so there's nothing to re-confirm.
    const handleCreateClick = () => {
        if (!client?.id) return;
        handleCreateEstimate();
    };

    // The option's PRICING fields from the form state — shared by create
    // (plus status / is_draft) and the edit-mode autosave (2026-09-19).
    const buildPricingFields = () => {
        const fields = {
        // truck picker; LOCAL jobs use the single crewSize/truckType.
        crew_size_needed: jobType === 'LONG_DISTANCE' ? loadCrewSize : crewSize,
        travel_crew_size: jobType === 'LONG_DISTANCE' ? travelCrewSize : 0,
        trucks_needed: jobType === 'LONG_DISTANCE' ? bolTruckType : truckType,
        // Both job types now persist extra trucks the same way.
        // OOS extras are allocation-only (no per-truck surcharge);
        // LOCAL extras drive the +$/hr surcharge in pricing.
        extra_trucks: JSON.stringify(jobType === 'LONG_DISTANCE' ? bolExtraTrucks : extraTrucks),
        // Waive the extra-truck surcharge while still requiring
        // the truck. LONG_DISTANCE doesn't have an hourly-rate
        // surcharge concept, so we always store false there.
        extra_truck_fee_waived: jobType === 'LONG_DISTANCE' ? false : extraTruckFeeWaived,
        truck_display: truckDisplay.trim() || null,
        // Pricing
        // Multi-day pricing: hours roll up from the day table and
        // hourly_rate is 0 (no single rate exists — per-day rates
        // live on planned_phases; display surfaces detect priced
        // phases and render the day breakdown instead).
        hourly_rate: jobType === 'LOCAL' ? (multiDay ? 0 : (parseFloat(hourlyRate) || 0)) : 0,
        estimated_hours: multiDay && jobType === 'LOCAL' ? dayHoursMax : (parseFloat(maxHours) || parseFloat(minHours) || 0),
        min_hours: multiDay && jobType === 'LOCAL' ? dayHoursMin : (parseFloat(minHours) || 0),
        max_hours: multiDay && jobType === 'LOCAL' ? dayHoursMax : (parseFloat(maxHours) || parseFloat(minHours) || 0),
        // LOCAL-only fees — not part of the OOS tariff formula. Zero
        // them on OOS saves so values leaked from the local tab's
        // state can't render phantom line items later (QA M2 BUG-4).
        travel_fee: jobType === 'LOCAL' ? (parseFloat(travelFee) || 0) : 0,
        materials_fee: jobType === 'LOCAL' ? (parseFloat(materialsFee) || 0) : 0,
        hold_fee: jobType === 'LOCAL' ? (parseFloat(holdFee) || 0) : 0,
        // Packing
        packing_needed: packingNeeded ? 'yes' : 'none',
        // (pack_date / pack_time are the move's — mirrored in.)
        packing_crew_size: packingNeeded ? packingCrewSize : 0,
        packing_rate: packingNeeded ? (parseFloat(packingRate) || 0) : 0,
        // Multi-day packing: hour columns are the SUM across all
        // packing days (day 1 + extras) so pricing math and every
        // display surface keep reading one number. Per-day hours
        // and rates live on the pack phases in planned_phases.
        packing_hours: packingNeeded ? ((parseFloat(packingMaxHours) || parseFloat(packingMinHours) || 0) + extraPackMaxHours) : 0,
        packing_min_hours: packingNeeded ? ((parseFloat(packingMinHours) || 0) + extraPackMinHours) : 0,
        packing_max_hours: packingNeeded ? ((parseFloat(packingMaxHours) || parseFloat(packingMinHours) || 0) + extraPackMaxHours) : 0,
        // Fees
        heavy_item_count: heavyItemCount,
        heavy_item_fee: heavyItemFee,
        removal_fee: parseFloat(removalFee) || 0,
        // BOL (long distance)
        bol_price: jobType === 'LONG_DISTANCE' ? (parseFloat(bolPrice) || 0) : 0,
        bol_reference: jobType === 'LONG_DISTANCE' ? bolReference : '',
        bol_deposit: jobType === 'LONG_DISTANCE' ? (parseFloat(bolDeposit) || 0) : 0,
        // Deposit provenance (sql/092) — only with an OOS amount; the
        // collector trio only when collected.
        ...(() => {
            const has = jobType === 'LONG_DISTANCE' && bolDepositNum > 0;
            const collected = has && bolDepositStatus === 'collected';
            return {
                bol_deposit_status: has ? (bolDepositStatus || 'requested') : null,
                bol_deposit_collected_by: collected ? (String(bolDepositCollectedBy || '').trim().toUpperCase().slice(0, 12) || null) : null,
                bol_deposit_method: collected ? (bolDepositMethod || null) : null,
                bol_deposit_collected_at: collected ? (bolDepositCollectedAt || new Date().toISOString()) : null,
            };
        })(),
        estimated_weight_lbs: jobType === 'LONG_DISTANCE' ? (parseFloat(estimatedWeight) || null) : null,
        // Totals
        total_estimate: totalMax,
        total_min: totalMin,
        total_max: totalMax,
        // Other (heavy_items / description come from the move)
        // (edit mode: the option keeps its own internal notes — the client
        // form's copy would overwrite them.)
        ...(isEdit ? {} : { internal_notes: form.internal_notes || '' }),
        // Phases — seeded by derivePhases() from the pricing inputs
        // above (unless the user manually edited the planner, in
        // which case `phases` already holds their edits). We pass
        // the array directly so upsertEstimate's auto-seed fallback
        // is a no-op and the user's chunks land exactly as shown.
        planned_phases: phases,
        // Lead-pipeline (sql/047 + sql/049). priority moved to
        // clients.priority — not set here. is_draft is the opt-out
        // flag keeping the estimate out of the callback queue.
        // quote_given_at stays null on creation — gets set by the
        // "Mark Verbal Quote Given" action when sales actually
        // calls the client.
    };
        return fields;
    };

    // ── Edit-mode autosave (2026-09-19, feedback.md 5C) ──────────────────
    // Fingerprint the pricing fields; a change after the seed debounces a
    // saveEstimate with the option's id. Job type flips write the move
    // (identity — rule 1); packing on lifts the move's packing flag as on
    // create. Money guards match the create path.
    const lastSavedFpRef = useRef(null);
    const autosave = useAutosave({
        enabled: isEdit && canEditPricing && !readOnly,
        save: async () => {
            if (!isEdit) return true;
            const fields = buildPricingFields();
            const negative = Object.entries({ hourly_rate: fields.hourly_rate, min_hours: fields.min_hours, max_hours: fields.max_hours, travel_fee: fields.travel_fee, hold_fee: fields.hold_fee, materials_fee: fields.materials_fee, removal_fee: fields.removal_fee, packing_rate: fields.packing_rate, bol_price: fields.bol_price, bol_deposit: fields.bol_deposit })
                .filter(([, v]) => (parseFloat(v) || 0) < 0);
            if (negative.length) return `${negative[0][0].replace(/_/g, ' ')} can't be negative`;
            try {
                if (current?.id) {
                    const movePatch = {};
                    const desiredType = jobType === 'LONG_DISTANCE' ? 'LONG_DISTANCE' : (current.job_type && current.job_type !== 'LONG_DISTANCE' ? current.job_type : 'LOCAL');
                    if (desiredType !== current.job_type) movePatch.job_type = desiredType;
                    if (packingNeeded && !['yes', 'partial', 'full'].includes(current.packing_needed || '')) movePatch.packing_needed = 'yes';
                    if (Object.keys(movePatch).length) await saveMove({ id: current.id, ...movePatch });
                }
                await saveEstimate({ ...editEstimate, ...fields, is_draft: isDraft });
                lastSavedFpRef.current = JSON.stringify({ ...fields, is_draft: isDraft });
                return true;
            } catch (e) { return e?.message || 'save failed'; }
        },
    });
    // Outside writes to the option's phases (Push to Dispatch stamps
    // move_job_ids; another user's edit) re-seed the planner so the next
    // autosave doesn't write the links away. Our own saves come back equal
    // to what we sent, so they don't trigger this.
    const extPhasesFp = isEdit ? JSON.stringify(editEstimate?.planned_phases || []) : '';
    useEffect(() => {
        if (!isEdit || seededIdRef.current !== editEstimate?.id) return;
        let saved = null; try { saved = lastSavedFpRef.current ? JSON.parse(lastSavedFpRef.current) : null; } catch { saved = null; }
        const ours = JSON.stringify(saved?.planned_phases || []);
        if (extPhasesFp === ours) return;
        const next = Array.isArray(editEstimate.planned_phases) ? normalizePhases(editEstimate.planned_phases) : [];
        setPhases(next);
        setPhasesDirty(next.length > 0);
        if (saved) { lastSavedFpRef.current = JSON.stringify({ ...saved, planned_phases: next }); }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [extPhasesFp]);
    // Parent hook-up (the Estimates page flushes a pending save before Push to Dispatch).
    useEffect(() => { if (autosaveRef) autosaveRef.current = autosave; });
    const pricingFp = isEdit ? JSON.stringify({ ...buildPricingFields(), is_draft: isDraft }) : '';
    useEffect(() => {
        if (!isEdit || seededIdRef.current !== editEstimate?.id) return;
        if (lastSavedFpRef.current === null) { lastSavedFpRef.current = pricingFp; return; } // first paint after the seed
        if (pricingFp === lastSavedFpRef.current) return;
        autosave.touch();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pricingFp]);

    const handleCreateEstimate = async (opts = {}) => {
        if (!client?.id) return;
        if (!current?.id) { showToast('No move on file yet — add one on the Client Details tab first', 'error'); return; }
        // Soft warning: OOS with zero crew creates fine but every phase
        // lands on the board with crew 0 (QA M2 BUG-9). One confirm, then
        // proceed if the operator really wants a placeholder.
        if (!opts.skipCrewCheck && jobType === 'LONG_DISTANCE' && (loadCrewSize || 0) === 0 && (travelCrewSize || 0) === 0) {
            setZeroCrewConfirm({});
            return;
        }
        // Money and hour fields can't be negative (QA M2 BUG-6 — a typed
        // "-500" saved fine and collapsed totals to $0). min="0" on inputs
        // only gates the spinner arrows, not typed values.
        const moneyFields = {
            'Hourly rate': hourlyRate, 'Min hours': minHours, 'Max hours': maxHours,
            'Travel fee': travelFee, 'Hold fee': holdFee, 'Materials fee': materialsFee, 'Removal fee': removalFee,
            'Packing rate': packingRate, 'Packing min hours': packingMinHours, 'Packing max hours': packingMaxHours,
            'BOL price': bolPrice, 'BOL deposit': bolDeposit, 'Estimated weight': estimatedWeight,
        };
        // Extra packing days ride the same negative-value guard.
        extraPackDays.forEach((d, i) => {
            moneyFields[`Packing day ${i + 2} rate`] = d.rate;
            moneyFields[`Packing day ${i + 2} min hours`] = d.minHours;
            moneyFields[`Packing day ${i + 2} max hours`] = d.maxHours;
        });
        const negative = Object.entries(moneyFields).filter(([, v]) => (parseFloat(v) || 0) < 0);
        if (negative.length) {
            setFormErrors(negative.map(([k]) => `${k} — can't be negative`));
            return;
        }
        setFormErrors([]);
        // Storage addresses (2026-08-27 → moves 2026-09-18): moving OUT of
        // storage → the move's origin IS the unit; moving INTO storage with a
        // facility picked → the move's destination IS the facility. Written
        // to the MOVE (rule 1), never to the option.
        const outUnit = storageMode === 'out' ? clientStorageTenancies.find(t => t.id === storageOutTenancyId) : null;
        const outUnitAddr = outUnit ? tenancyAddress(outUnit, storageUnitsById, storageFacilitiesById) : '';
        const intoDestAddr = storageMode !== 'into' ? ''
            : storageOwnership === 'bbm'
                ? (storageFacilityRow ? (storageFacilityRow.address || storageFacilityRow.name) : '')
                : storageFacility.trim();
        const effectiveDate = current.load_date || null;
        if (!effectiveDate) {
            const proceed = await confirm({
                title: `Create ${client?.name || 'this client'}'s estimate with no move date?`,
                body: `The estimate is created and saved to ${client?.name || 'the client'}'s file, but it won't appear on the dispatch board until a date is set on the Client Details tab.`,
                confirmLabel: 'Create anyway',
                cancelLabel: 'Go back',
            });
            if (!proceed) return;
        }
        // Move identity this option implies. Job type: OOS pricing → the move
        // is LONG_DISTANCE; local pricing keeps the move's real service type
        // (Delivery / Pickup / Labor…) unless it was LONG_DISTANCE → LOCAL
        // (Jensen 2026-06-26: deliveries were landing on the board as "Local").
        const movePatch = {};
        const desiredType = jobType === 'LONG_DISTANCE'
            ? 'LONG_DISTANCE'
            : (current.job_type && current.job_type !== 'LONG_DISTANCE' ? current.job_type : 'LOCAL');
        if (desiredType !== current.job_type) movePatch.job_type = desiredType;
        if (outUnitAddr && outUnitAddr !== current.origin_address) movePatch.origin_address = outUnitAddr;
        if (intoDestAddr && intoDestAddr !== current.destination_address) movePatch.destination_address = intoDestAddr;
        // Into storage as its own local move = the "split" shape (§2a): the
        // out-move later chains to it via "Deliver out of storage".
        if (storageMode === 'into' && !current.storage_mode) movePatch.storage_mode = 'split';
        setCreating(true);
        try {
            if (Object.keys(movePatch).length) await saveMove({ id: current.id, ...movePatch });
            // Pricing only. Addresses, dates, job type, heavy items and the
            // description are the MOVE's — db.createOption fills the option's
            // mirror columns from it (and the trigger keeps them in step).
            const estimateData = { status: 'building', ...buildPricingFields(), is_draft: isDraft };
            // New OPTION on the current move: shares the move number, gets
            // the next letter, identity filled from the move. refreshMove
            // pulls the move + its options + the client back into the store.
            // packing_needed is the MOVE's (mirrored onto every option, so the
            // value in estimateData above is overwritten on insert). Pricing
            // packing in is a statement that the client needs it: lift the
            // move from tbd/none to 'yes'. Pricing it out never downgrades
            // the move — an option without packing is still an option.
            const mvPack = current.packing_needed;
            if (packingNeeded && !['yes', 'partial', 'full'].includes(mvPack || '')) await saveMove({ id: current.id, packing_needed: 'yes' });
            const created = await db.createOption(current.id, estimateData);
            await refreshMove(current.id);
            // Storage linkage (sql/084-085): INTO storage → create the unit
            // as QUOTED (goes active automatically when this estimate
            // completes); OUT of storage → stamp this estimate as the
            // unit's move-out half. Best-effort — a storage write failure
            // never rolls back the estimate.
            if (storageMode === 'into' && created?.id) {
                try {
                    const isBbm = storageOwnership === 'bbm';
                    await saveStorageTenancy({
                        client_id: client.id,
                        ownership: storageOwnership,
                        facility_id: isBbm ? (storageFacilityRow?.id || null) : null,
                        unit_ids: isBbm ? storagePickedUnitIds : [],
                        facility_name: isBbm ? (storageFacilityRow?.name || '') : storageFacility.trim(),
                        facility_address: isBbm ? (storageFacilityRow?.address || '') : '',
                        unit_number: isBbm ? '' : storageUnitNumber.trim(),
                        status: 'quoted',
                        in_estimate_id: created.id,
                    });
                } catch (se) {
                    showToast('Estimate created, but the storage record failed: ' + se.message, 'error');
                }
            } else if (storageMode === 'out' && outUnit && created?.id) {
                try {
                    await patchStorageTenancy(outUnit.id, { out_estimate_id: created.id });
                } catch (se) {
                    showToast('Estimate created, but linking the storage record failed: ' + se.message, 'error');
                }
            }
            showToast(isDraft ? 'Estimate created as draft' : 'Estimate created — added to callback queue');
            // Deposit transfer (opted in via the same-move dialog). The
            // estimate exists either way — a transfer failure is surfaced
            // loudly but doesn't roll anything back; the row Transfer
            // control on the Estimates page is the retry path.
            if (transferDeposit && depositSource && created?.id) {
                try {
                    await db.transferDepositTrail(depositSource.id, created.id);
                    showToast(`Deposit + signed terms transferred from ${depositSource.job_number}`);
                } catch (te) {
                    showToast(`Estimate created, but deposit transfer failed: ${te.message}. Use the Transfer button on the Estimates page to retry.`, 'error');
                }
                setTransferDeposit(false);
            }
            if (onEstimateCreated) onEstimateCreated(created);
        } catch (e) {
            console.error('Estimate creation error:', e);
            showToast('Error: ' + (e.message || 'Unknown error'), 'error');
        } finally {
            setCreating(false);
        }
    };

    // Swap pickup ⇄ delivery direction. For OOS this is the "bringing someone
    // HERE" (load out of state, unload local) vs "taking them OUT" (load local,
    // unload out of state) distinction. Swaps the addresses AND the per-end
    // location details so the whole origin/destination pair flips together.
    const swapDirection = () => {
        const snap = { ...form };
        const pairs = [
            ['origin_address', 'destination_address'],
            ['from_sqft', 'to_sqft'], ['from_levels', 'to_levels'],
            ['from_stairs', 'to_stairs'], ['from_stairs_steps', 'to_stairs_steps'],
            ['from_elevator', 'to_elevator'], ['from_apartment', 'to_apartment'],
            ['from_apartment_floor', 'to_apartment_floor'],
            ['from_driveway', 'to_driveway'], ['from_walk_distance', 'to_walk_distance'],
        ];
        pairs.forEach(([a, b]) => { set(a, snap[b]); set(b, snap[a]); });
    };

    return (
        <div className="space-y-5">
            <FormErrorBanner items={formErrors} />

            {/* What's being priced (2026-09-18): the current move's dates,
                route, type and status — read-only here. Dates and addresses
                are edited on the Client Details tab (rule 1). */}
            {isEdit ? (
                <div className="flex items-center justify-between gap-2 flex-wrap">
                    <span className="text-[11px] font-bold text-slate-500 uppercase tracking-wider">Option {editEstimate.option_letter || ''} pricing</span>
                    <AutosaveStatus status={autosave.status} savedAt={autosave.savedAt} error={autosave.error} />
                </div>
            ) : current ? (
                <MoveHeaderCard
                    move={current}
                    compact
                    disabled
                    title={`Pricing move ${current.move_number ? current.move_number.replace(/^BBM-\d{4}-/, '') : ''}${moveOptions.length ? ` · option ${String.fromCharCode(65 + moveOptions.length)}` : ''}`}
                    extra={<Button size="sm" variant="tertiary" onClick={() => showToast('Edit dates and addresses on the Details tab')}>Edit</Button>}
                />
            ) : (
                <div className="rounded-lg border border-dashed border-amber-300 bg-amber-50 p-3 text-[12.5px] text-amber-800">
                    No move on file for this client yet — add one on the Client Details tab (+ New move) before pricing.
                </div>
            )}
            {initialFrom?.est && prefillNote && (
                <div className="rounded-lg border border-indigo-200 bg-indigo-50/70 px-3 py-2 text-[12px] text-indigo-800 flex items-center justify-between gap-2 flex-wrap">
                    <span>{prefillNote}</span>
                    <Button size="sm" variant="tertiary" onClick={() => setPrefillNote('')}>Dismiss</Button>
                </div>
            )}

            {/* ===== JOB TYPE TOGGLE ===== */}
            <div>
                <h3 className="text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-3">Job Type</h3>
                <div className="flex gap-2">
                    <button onClick={() => setJobType('LOCAL')}
                        data-testid="pricing-jobtype-LOCAL"
                        className={`flex-1 py-3 rounded-xl text-sm font-bold border-2 transition-all ${jobType === 'LOCAL' ? 'bg-red-600 text-white border-red-600 shadow-md' : 'bg-white text-slate-600 border-slate-200 hover:border-red-300'}`}>
                        Local / Hourly
                    </button>
                    <button onClick={() => setJobType('LONG_DISTANCE')}
                        data-testid="pricing-jobtype-LONG_DISTANCE"
                        className={`flex-1 py-3 rounded-xl text-sm font-bold border-2 transition-all ${jobType === 'LONG_DISTANCE' ? 'bg-blue-600 text-white border-blue-600 shadow-md' : 'bg-white text-slate-600 border-slate-200 hover:border-blue-300'}`}>
                        Out-of-State (BOL)
                    </button>
                </div>
            </div>

            {/* ===== LOCAL HOURLY ===== */}
            {jobType === 'LOCAL' && canEditPricing && (
                <>
                    {/* Crew Size */}
                    <div>
                        <h3 className="text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-3 flex items-center gap-2"><IconUsers size={13}/>Crew & Equipment</h3>
                        <FormField label="Crew Size">
                            <div className="flex items-center gap-2">
                                {[2, 3, 4, 5, 6].map(n => (
                                    <button key={n} type="button" onClick={() => setCrewSize(n)}
                                        className={`w-10 h-10 rounded-lg text-sm font-bold border-2 transition-all ${crewSize === n ? 'bg-red-600 text-white border-red-600 shadow-md' : 'bg-white text-slate-600 border-slate-300 hover:border-red-300'}`}>
                                        {n}
                                    </button>
                                ))}
                                {crewSize > 0 && <span className="text-xs text-slate-400 ml-1">{crewSize} movers</span>}
                            </div>
                        </FormField>
                        <FormField label="Primary Truck">
                            <div className="flex flex-wrap gap-1.5">
                                {TRUCK_TYPE_OPTIONS.map(t => (
                                    <button key={t.value} type="button" onClick={() => setTruckType(t.value)}
                                        className={`px-3 py-2 rounded-lg text-xs font-semibold border-2 transition-all ${truckType === t.value ? 'bg-blue-600 text-white border-blue-600 shadow-md' : 'bg-white text-slate-600 border-slate-300 hover:border-blue-300'}`}>
                                        {t.label}
                                    </button>
                                ))}
                            </div>
                        </FormField>
                        {truckType !== 'none' && (
                            <div className="mt-2">
                                <div className="flex items-center gap-2 mb-1.5">
                                    <span className="text-[10px] font-bold text-slate-400 uppercase">Extra Trucks</span>
                                    <span className="text-[10px] text-slate-400">(+${extraTruckFeePerHr}/hr each)</span>
                                </div>
                                <div className="flex items-center gap-2 flex-wrap">
                                    {/* TRUCK_EXTRA_SIZES is the single source of truth for
                                        which sizes can be added as extras. 20ft added per
                                        May 2026 sales-lead request. */}
                                    {TRUCK_EXTRA_SIZES.map(sz => (
                                        <Button key={sz.value} variant="tertiary" size="sm" onClick={() => setExtraTrucks(prev => [...prev, sz.value])}>
                                            + {sz.label}
                                        </Button>
                                    ))}
                                    {extraTrucks.map((t, i) => (
                                        <span key={i} className="inline-flex items-center gap-1 px-2.5 py-1.5 bg-blue-100 text-blue-700 rounded-lg text-xs font-semibold">
                                            {truckSizeShortLabel(t)} truck
                                            <button onClick={() => setExtraTrucks(prev => prev.filter((_, j) => j !== i))} className="text-blue-400 hover:text-red-500 ml-0.5 font-bold">&times;</button>
                                        </span>
                                    ))}
                                </div>
                                {extraTrucks.length > 0 && (
                                    <>
                                        <p className="text-[10px] text-blue-500 mt-1">
                                            {extraTrucks.length} extra truck{extraTrucks.length > 1 ? 's' : ''}{' '}
                                            {extraTruckFeeWaived
                                                ? <span className="text-amber-600 font-semibold">— surcharge waived</span>
                                                : <>= +${extraTruckSurchargePerHr}/hr added to rate</>}
                                        </p>
                                        <button
                                            type="button"
                                            onClick={() => setExtraTruckFeeWaived(v => !v)}
                                            aria-pressed={extraTruckFeeWaived}
                                            className={`mt-2 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-bold border transition-colors ${
                                                extraTruckFeeWaived
                                                    ? 'bg-amber-500 border-amber-500 text-white hover:bg-amber-600'
                                                    : 'bg-white border-slate-300 text-slate-700 hover:bg-slate-50'
                                            }`}>
                                            {extraTruckFeeWaived && (
                                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
                                            )}
                                            {extraTruckFeeWaived ? 'Surcharge waived' : 'Waive extra-truck surcharge'}
                                        </button>
                                        <span className="block text-[10px] text-slate-400 mt-1">Truck still required, just not billed.</span>
                                    </>
                                )}
                            </div>
                        )}
                        {/* Client-facing truck wording (2026-09-21). The document
                            never prints sizes on its own any more — day-of swaps
                            (16' → 20') were turning into billing arguments. */}
                        {truckType !== 'none' && (
                            <FormField label="Truck wording on the written estimate" className="mt-3">
                                <input
                                    type="text"
                                    value={truckDisplay}
                                    onChange={e => setTruckDisplay(e.target.value)}
                                    placeholder={extraTrucks.length ? `${extraTrucks.length + 1} trucks` : 'truck'}
                                    maxLength={80}
                                    aria-label="Truck wording on the written estimate"
                                    className={inputCls}
                                />
                                <span className="block text-[10px] text-slate-400 mt-1">
                                    Reads "{crewSize || 'N'}-person moving crew — with {truckDisplay.trim() || (extraTrucks.length ? `${extraTrucks.length + 1} trucks` : 'truck')}". Leave blank for the plain wording; type "two 26' trucks" only when you're promising it.
                                </span>
                            </FormField>
                        )}
                    </div>

                    {/* Pricing */}
                    <div>
                        <div className="flex items-center justify-between mb-3">
                            <h3 className="text-[11px] font-bold text-slate-400 uppercase tracking-wider flex items-center gap-2"><IconDollarSign size={13}/>Labor Pricing</h3>
                            {/* Multi-day pricing toggle — load/hold-overnight/
                                unload moves price each working day with its own
                                crew + hours + rate. Day rows come from the
                                Phase Planner below. */}
                            <button type="button" onClick={() => setMultiDay(!multiDay)}
                                className={`text-xs font-bold px-3 py-1 rounded-full transition-all ${multiDay ? 'bg-indigo-100 text-indigo-700' : 'bg-slate-100 text-slate-400 hover:text-slate-600'}`}>
                                {multiDay ? 'Multi-Day Pricing On' : 'Price Per Day'}
                            </button>
                        </div>

                        {multiDay && (
                            <div className="bg-indigo-50/60 border border-indigo-200 rounded-xl p-4 mb-3 space-y-2">
                                <p className="text-[11px] text-indigo-500 mb-1">
                                    Day rows mirror the Phase Planner (add/remove/reorder phases down there). Pack days price under Packing Services; holds aren't billed.
                                </p>
                                {dayPhases.length === 0 && (
                                    <p className="text-xs text-slate-400">No billable phases yet — add Load / Unload / Move phases in the Phase Planner below.</p>
                                )}
                                {dayPhases.map((p, i) => {
                                    const lineMin = (Number(p.est_min_hours) || 0) * ((Number(p.hourly_rate) || 0) + extraTruckSurchargePerHr);
                                    const lineMax = (Number(p.est_max_hours) || Number(p.est_min_hours) || 0) * ((Number(p.hourly_rate) || 0) + extraTruckSurchargePerHr);
                                    return (
                                        <div key={p.sequence} className="grid grid-cols-[auto_128px_128px_128px_148px_1fr] items-center gap-2 bg-white border border-indigo-100 rounded-lg px-3 py-2">
                                            <span className="text-[11px] font-bold text-indigo-700 uppercase w-16">
                                                Day {i + 1}
                                                <span className="block text-[9px] font-semibold text-slate-400 normal-case">{kindShortLabel(p.kind)}</span>
                                            </span>
                                            <span className="text-[9px] font-bold text-slate-400 uppercase">Crew
                                                <NumberField size="sm" step={1} min={1} max={8} className="mt-0.5" inputClassName="font-bold" value={p.crew_size || ''} onChange={v => updateDayPricing(p.sequence, 'crew_size', parseInt(v) || 0)} placeholder="0" ariaLabel={`Day ${i + 1} crew`} />
                                            </span>
                                            <span className="text-[9px] font-bold text-slate-400 uppercase">Min hrs
                                                <NumberField size="sm" step={0.5} className="mt-0.5" value={p.est_min_hours || ''} onChange={v => updateDayPricing(p.sequence, 'est_min_hours', v)} placeholder="0" ariaLabel={`Day ${i + 1} min hours`} />
                                            </span>
                                            <span className="text-[9px] font-bold text-slate-400 uppercase">Max hrs
                                                <NumberField size="sm" step={0.5} className="mt-0.5" value={p.est_max_hours || ''} onChange={v => updateDayPricing(p.sequence, 'est_max_hours', v)} placeholder="0" ariaLabel={`Day ${i + 1} max hours`} />
                                            </span>
                                            <span className="text-[9px] font-bold text-slate-400 uppercase">Rate
                                                <NumberField size="sm" step={5} prefix="$" className="mt-0.5" inputClassName="font-bold" value={p.hourly_rate || ''} onChange={v => updateDayPricing(p.sequence, 'hourly_rate', v)} placeholder="0" ariaLabel={`Day ${i + 1} hourly rate`} />
                                            </span>
                                            <span className="text-right text-sm font-bold text-slate-700 whitespace-nowrap">
                                                {lineMax > 0 ? (lineMin !== lineMax ? `$${lineMin.toLocaleString()}–$${lineMax.toLocaleString()}` : `$${lineMax.toLocaleString()}`) : '—'}
                                            </span>
                                        </div>
                                    );
                                })}
                                {dayLaborMax > 0 && (
                                    <div className="text-right text-sm font-bold text-indigo-800 pt-1">
                                        Labor total: {dayLaborMin !== dayLaborMax ? `$${dayLaborMin.toLocaleString()} – $${dayLaborMax.toLocaleString()}` : `$${dayLaborMax.toLocaleString()}`}
                                        <span className="text-[10px] font-medium text-indigo-400 ml-1.5">({dayHoursMin}{dayHoursMax !== dayHoursMin ? `–${dayHoursMax}` : ''} hrs total)</span>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Rate (auto-filled, editable). With a billed extra-truck
                            surcharge the FIELD shows the effective rate (Jaylen
                            2026-09-22: "the rate changing in the actual rate field
                            is more clear") — what's stored stays the BASE rate
                            (hourly_rate), so typing 350 here sets base = 350 − 25.
                            The explainer underneath shows the arithmetic. */}
                        {!multiDay && (() => {
                            const surchargeOn = extraTrucks.length > 0 && !extraTruckFeeWaived && extraTruckSurchargePerHr > 0;
                            const shown = surchargeOn ? (hourlyRate ? Number(hourlyRate) + extraTruckSurchargePerHr : '') : (hourlyRate || '');
                            const onRate = (v) => {
                                if (!surchargeOn) { setHourlyRate(v); return; }
                                const n = parseFloat(v);
                                setHourlyRate(Number.isFinite(n) ? Math.max(0, n - extraTruckSurchargePerHr) : '');
                            };
                            return (
                        <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 mb-3">
                            <div className="flex items-center justify-between mb-2">
                                <span className="text-xs font-bold text-slate-500">{surchargeOn ? 'Hourly Rate · incl. extra truck' : 'Base Hourly Rate'}</span>
                                {hourlyRate > 0 && <span className="text-[10px] text-green-600 font-medium">Auto-filled from rate table</span>}
                            </div>
                            <NumberField step={5} prefix="$" suffix="/hr" inputClassName="text-lg font-bold" value={shown} onChange={onRate} placeholder="0" ariaLabel={surchargeOn ? 'Hourly rate including extra-truck surcharge' : 'Base hourly rate'} />
                            {extraTrucks.length > 0 && (
                                extraTruckFeeWaived ? (
                                    <div className="mt-2 text-[13px] text-amber-600 font-medium">
                                        Extra-truck surcharge waived — the rate stays at <span className="font-bold">${effectiveRate}/hr</span>
                                    </div>
                                ) : (
                                    <div className="mt-2 text-[13px] text-blue-600 font-medium">
                                        ${hourlyRate || 0}/hr base + ${extraTruckSurchargePerHr}/hr extra truck{extraTrucks.length > 1 ? 's' : ''} = <span className="font-bold">${effectiveRate}/hr</span>
                                    </div>
                                )
                            )}
                        </div>
                            );
                        })()}

                        {/* Hour range (single-day mode only) */}
                        {!multiDay && (
                        <div className="grid grid-cols-2 gap-3 mb-3">
                            <FormField label="Min Hours">
                                <NumberField step={0.5} value={minHours} onChange={v => setMinHours(v)} placeholder="e.g. 4" />
                            </FormField>
                            <FormField label="Max Hours">
                                <NumberField step={0.5} value={maxHours} onChange={v => setMaxHours(v)} placeholder="e.g. 6" />
                            </FormField>
                        </div>
                        )}

                        {/* Labor subtotal preview (single-day mode only) */}
                        {!multiDay && (minHours || maxHours) && hourlyRate > 0 && (
                            <div className="bg-slate-100 rounded-lg p-3 mb-3 text-center">
                                <span className="text-xs text-slate-500">Labor: </span>
                                <span className="text-sm font-bold text-slate-800">
                                    {isRange
                                        ? `$${laborMin.toLocaleString()} – $${laborMax.toLocaleString()}`
                                        : `$${laborMin.toLocaleString()}`
                                    }
                                </span>
                                <span className="text-xs text-slate-400 ml-1">
                                    ({minHours}{maxHours && maxHours !== minHours ? `–${maxHours}` : ''} hrs × ${effectiveRate}/hr)
                                </span>
                            </div>
                        )}

                        {/* Travel & Materials */}
                        <div className="grid grid-cols-2 gap-3">
                            <FormField label="Travel Fee ($)">
                                <NumberField step={5} prefix="$" value={travelFee || ''} onChange={v => setTravelFee(v)} placeholder="0" />
                            </FormField>
                            <FormField label="Materials Fee ($)">
                                <NumberField step={5} prefix="$" value={materialsFee || ''} onChange={v => setMaterialsFee(v)} placeholder="0" />
                            </FormField>
                        </div>
                        {/* Hold fee — below travel (Jaylen 2026-09-11): flat charge
                            for holding the loaded truck overnight between a load
                            day and an unload day. Hold PHASES stay non-billable
                            hours; this is the fee for the hold itself. */}
                        <div className="grid grid-cols-2 gap-3">
                            <FormField label="Hold Fee ($)">
                                <NumberField step={5} prefix="$" value={holdFee || ''} onChange={v => setHoldFee(v)} placeholder="0" />
                            </FormField>
                            <div className="text-[11px] text-slate-400 self-end pb-2">Flat fee for keeping the loaded truck between days (load → hold → unload).</div>
                        </div>
                    </div>
                </>
            )}

            {/* ===== OUT-OF-STATE BOL ===== */}
            {jobType === 'LONG_DISTANCE' && canEditPricing && (
                <>
                    {/* Move direction — flip pickup/delivery. Load out of state +
                        unload local = bringing someone HERE; load local + unload
                        out of state = taking them OUT. */}
                    <div className="bg-white border border-blue-200 rounded-xl p-3 flex items-center justify-between gap-3">
                        <div className="min-w-0">
                            <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-0.5">Move Direction</div>
                            <div className="text-xs text-slate-700 truncate">
                                <span className="font-semibold text-red-600">{form.origin_address ? form.origin_address.split(',')[0] : 'Pickup'}</span>
                                <span className="text-slate-400"> → </span>
                                <span className="font-semibold text-blue-600">{form.destination_address ? form.destination_address.split(',')[0] : 'Delivery'}</span>
                            </div>
                        </div>
                        <Button variant="secondary" size="sm" className="flex-shrink-0" onClick={swapDirection}
                            title="Swap pickup & delivery — bringing someone here vs taking them out of state"
                            leftIcon={<span className="text-sm leading-none">⇄</span>}>
                            Swap direction
                        </Button>
                    </div>

                    {/* BOL Crew & Truck allocation — mirrors the local-hourly crew/truck
                        block so we can still schedule the move on the dispatch board. */}
                    <div>
                        <h3 className="text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-3 flex items-center gap-2"><IconUsers size={13}/>BOL Crew & Truck Allocation</h3>
                        <div className="bg-blue-50/60 border border-blue-200 rounded-xl p-4 space-y-4">
                            <FormField label="Load Crew (on site for wrap/load)">
                                <div className="flex items-center gap-2 flex-wrap">
                                    {[2, 3, 4, 5, 6].map(n => (
                                        <button key={n} type="button" onClick={() => setLoadCrewSize(n)}
                                            className={`w-10 h-10 rounded-lg text-sm font-bold border-2 transition-all ${loadCrewSize === n ? 'bg-blue-600 text-white border-blue-600 shadow-md' : 'bg-white text-slate-600 border-slate-300 hover:border-blue-300'}`}>
                                            {n}
                                        </button>
                                    ))}
                                    {loadCrewSize > 0 && <span className="text-xs text-slate-400 ml-1">{loadCrewSize} on load day</span>}
                                </div>
                            </FormField>
                            <FormField label="Travel Crew (going on the trip)">
                                <div className="flex items-center gap-2 flex-wrap">
                                    {[1, 2, 3, 4].map(n => (
                                        <button
                                            key={n}
                                            type="button"
                                            onClick={() => setTravelCrewSize(n)}
                                            disabled={loadCrewSize > 0 && n > loadCrewSize}
                                            className={`w-10 h-10 rounded-lg text-sm font-bold border-2 transition-all disabled:opacity-30 disabled:cursor-not-allowed ${travelCrewSize === n ? 'bg-indigo-600 text-white border-indigo-600 shadow-md' : 'bg-white text-slate-600 border-slate-300 hover:border-indigo-300'}`}>
                                            {n}
                                        </button>
                                    ))}
                                    {travelCrewSize > 0 && <span className="text-xs text-slate-400 ml-1">{travelCrewSize} travelling</span>}
                                </div>
                                {loadCrewSize > 0 && travelCrewSize > loadCrewSize && (
                                    <FieldError>Max {loadCrewSize}</FieldError>
                                )}
                            </FormField>
                            {/* Primary Truck — same picker as LOCAL (May 2026
                                unification). Persists to estimates.trucks_needed. */}
                            <FormField label="Primary Truck">
                                <div className="flex flex-wrap gap-1.5">
                                    {TRUCK_TYPE_OPTIONS.map(t => (
                                        <button key={t.value} type="button" onClick={() => setBolTruckType(t.value)}
                                            data-testid="oos-primary-truck-option" data-value={t.value}
                                            className={`px-3 py-2 rounded-lg text-xs font-semibold border-2 transition-all ${bolTruckType === t.value ? 'bg-blue-600 text-white border-blue-600 shadow-md' : 'bg-white text-slate-600 border-slate-300 hover:border-blue-300'}`}>
                                            {t.label}
                                        </button>
                                    ))}
                                </div>
                            </FormField>
                            {/* Extra trucks on OOS — purely an allocation concern (BOL
                                pricing is flat tariff, no per-truck surcharge applies),
                                so unlike the LOCAL block we don't show fee math here.
                                Saved to estimates.extra_trucks so dispatch allocates
                                them the same way. */}
                            {bolTruckType !== 'none' && (
                                <div>
                                    <div className="flex items-center gap-2 mb-1.5">
                                        <span className="text-[10px] font-bold text-slate-400 uppercase">Extra Trucks</span>
                                        <span className="text-[10px] text-slate-400">(allocation only — BOL is flat-rate)</span>
                                    </div>
                                    <div className="flex items-center gap-2 flex-wrap">
                                        {TRUCK_EXTRA_SIZES.map(sz => (
                                            <Button key={sz.value} variant="tertiary" size="sm" onClick={() => setBolExtraTrucks(prev => [...prev, sz.value])}
                                                data-testid="oos-extra-truck-add" data-value={sz.value}>
                                                + {sz.label}
                                            </Button>
                                        ))}
                                        {bolExtraTrucks.map((t, i) => (
                                            <span key={i} className="inline-flex items-center gap-1 px-2.5 py-1.5 bg-blue-100 text-blue-700 rounded-lg text-xs font-semibold">
                                                {truckSizeShortLabel(t)} truck
                                                <button onClick={() => setBolExtraTrucks(prev => prev.filter((_, j) => j !== i))} className="text-blue-400 hover:text-red-500 ml-0.5 font-bold">&times;</button>
                                            </span>
                                        ))}
                                    </div>
                                    {bolExtraTrucks.length > 0 && (
                                        <p className="text-[10px] text-blue-500 mt-1">
                                            {bolExtraTrucks.length} extra truck{bolExtraTrucks.length > 1 ? 's' : ''} — will be pre-allocated on the dispatch board.
                                        </p>
                                    )}
                                </div>
                            )}
                            {packingNeeded && (
                                <FormField label="Packing Crew">
                                    <div className="flex items-center gap-2 flex-wrap">
                                        {[1, 2, 3, 4].map(n => (
                                            <button key={n} type="button" onClick={() => setPackingCrewSize(n)}
                                                className={`w-10 h-10 rounded-lg text-sm font-bold border-2 transition-all ${packingCrewSize === n ? 'bg-amber-500 text-white border-amber-500 shadow-md' : 'bg-white text-slate-600 border-slate-300 hover:border-amber-300'}`}>
                                                {n}
                                            </button>
                                        ))}
                                        {packingCrewSize > 0 && <span className="text-xs text-slate-400 ml-1">{packingCrewSize} packer{packingCrewSize !== 1 ? 's' : ''}</span>}
                                    </div>
                                </FormField>
                            )}
                        </div>
                    </div>

                    {/* BOL Pricing (tariff + pre-paid deposit) */}
                    <div>
                        <h3 className="text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-3 flex items-center gap-2"><IconTruck size={13}/>Bill of Lading</h3>
                        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 space-y-3">
                            {/* Weight first — it's what the brothers know at
                                review time. Tariff $/BOL#/deposit get filled
                                later (edit the estimate from the Estimates
                                page) once the tariff lookup is done; estimate
                                stays in Building until then. */}
                            <FormField label="Estimated Shipment Weight (lbs)">
                                <NumberField step={100} suffix="lbs" inputClassName="font-bold" value={estimatedWeight} onChange={v => setEstimatedWeight(v)} placeholder="e.g. 6500" />
                                <p className="text-[10px] text-blue-500 mt-1">Feeds the tariff lookup. Tariff price, BOL # and deposit can be added later — the estimate stays in Building until pricing is done.</p>
                            </FormField>
                            <FormField label="BOL Move Price (flat rate from tariff)">
                                <NumberField step={50} prefix="$" inputClassName="text-lg font-bold" value={bolPrice} onChange={v => setBolPrice(v)} placeholder="Total from tariff" />
                            </FormField>
                            <FormField label="BOL / Reference #">
                                <input className={inputCls + ' border-blue-200'} value={bolReference} onChange={e => setBolReference(e.target.value)} placeholder="BOL number or reference" />
                            </FormField>
                            <FormField label="Deposit">
                                <NumberField step={50} prefix="−$" inputClassName="font-semibold" value={bolDeposit} onChange={v => setBolDeposit(v)} placeholder="0" />
                                <p className="text-[10px] text-blue-500 mt-1">Deposit requested on the BOL (or already collected) — netted out of the total below.</p>
                            </FormField>
                            {/* Deposit provenance (sql/092): requested vs collected, and
                                by whom / how. Only meaningful once there's an amount. */}
                            {bolDepositNum > 0 && (
                                <div className="space-y-2">
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <ChoiceKeys size="sm" ariaLabel="Deposit status" items={BOL_DEPOSIT_STATUS_ITEMS} value={bolDepositStatus}
                                            onChange={v => { if (v === 'collected') markBolCollected(); else setBolDepositStatus(v); }} />
                                        {bolDepositStatus === 'requested' && (
                                            <Button variant="secondary" size="sm" onClick={markBolCollected}>Mark collected</Button>
                                        )}
                                    </div>
                                    {bolDepositStatus === 'collected' && (
                                        <div className="grid grid-cols-2 gap-3">
                                            <FormField label="Collected by (initials)">
                                                <input className={inputCls + ' border-blue-200 uppercase'} maxLength={12} value={bolDepositCollectedBy}
                                                    onChange={e => setBolDepositCollectedBy(e.target.value)}
                                                    onBlur={e => setBolDepositCollectedBy(e.target.value.trim().toUpperCase())}
                                                    placeholder="JB" />
                                            </FormField>
                                            <FormField label="Method">
                                                <ChoiceKeys size="sm" ariaLabel="Deposit method" items={BOL_DEPOSIT_METHOD_ITEMS} value={bolDepositMethod || ''} onChange={v => setBolDepositMethod(v)} />
                                            </FormField>
                                            {bolDepositCollectedAt && (
                                                <div className="col-span-2 text-[10px] text-slate-400 -mt-1">
                                                    on {(() => { try { return format(parseISO(bolDepositCollectedAt), 'MMM d, yyyy'); } catch { return bolDepositCollectedAt; } })()}
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            )}
                            {bolDepositNum > 0 && parseFloat(bolPrice) > 0 && (
                                <div className="text-[11px] text-emerald-700 font-semibold bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2">
                                    Tariff ${parseFloat(bolPrice).toLocaleString()} − Deposit ${bolDepositNum.toLocaleString()} = <span className="font-bold">${Math.max(0, (parseFloat(bolPrice) || 0) - bolDepositNum).toLocaleString()}</span> outstanding
                                    <span className="font-medium text-emerald-600">{bolDepositSuffix}</span>
                                </div>
                            )}
                            <p className="text-[11px] text-blue-500">Weight-based pricing from state tariff. Packing services billed separately below if needed.</p>
                        </div>
                    </div>
                </>
            )}

            {/* ===== PACKING LINE (both job types) ===== */}
            {canEditPricing && (
                <div>
                    <div className="flex items-center justify-between mb-2">
                        <h3 className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">Packing Services</h3>
                        <button onClick={async () => {
                                // Warn before removing — turning packing off drops
                                // the dated pack phase from the planner.
                                if (packingNeeded && (packDate || extraPackDays.length > 0 || phases.some(p => kindSegments(p.kind).includes('pack')))) {
                                    if (!(await confirm({ title: `Remove packing from ${client?.name || 'this client'}'s estimate?`, body: `The packing day${extraPackDays.length > 0 ? 's' : ''} and pack phase${extraPackDays.length > 0 ? 's' : ''} are cleared from the planner. The move phases and the rest of the estimate are kept.`, confirmLabel: 'Remove packing', danger: true }))) return;
                                }
                                if (packingNeeded) {
                                    setExtraPackDays([]);
                                    // Strip every pack segment from the planner even when it
                                    // was hand-edited (phasesDirty skips the re-derive, so the
                                    // pack phase used to survive "Remove packing") — a pack-only
                                    // day goes away, a pack+load combo keeps just its load half.
                                    // Jensen 2026-09-15 (Carolyn Franz second estimate).
                                    setPhases(prev => normalizePhases(prev.flatMap(p => {
                                        if (!kindIsPack(p.kind)) return [p];
                                        const rest = kindSegments(p.kind).filter(s => s !== 'pack');
                                        if (rest.length === 0) return [];
                                        const kind = kindId(rest);
                                        return [{ ...p, kind, ...placePhasePricing(kind, phaseLaborPricing(p), null) }];
                                    })));
                                    setPackingMinHours(''); setPackingMaxHours(''); setPackingRate(0);
                                }
                                setPackingNeeded(!packingNeeded);
                            }}
                            className={`text-xs font-bold px-3 py-1 rounded-full transition-all ${packingNeeded ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-400 hover:text-slate-600'}`}>
                            {packingNeeded ? 'Packing Added' : '+ Add Packing'}
                        </button>
                    </div>
                    {packingNeeded && (
                        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 space-y-3">
                            {/* Day 1 — the legacy single-day fields. When extra
                                days exist it gets a "Day 1" header so the card
                                reads as a day-by-day breakdown. */}
                            {extraPackDays.length > 0 && (
                                <div className="text-[10px] font-black text-amber-600 uppercase tracking-wider">Packing Day 1</div>
                            )}
                            {/* Pack day is the MOVE's (2026-09-18) — read here, edited
                                on the Details tab's trip strip ("+ Pack day"). */}
                            <div className="flex items-center justify-between gap-2 flex-wrap rounded-lg border border-amber-200 bg-white/70 px-3 py-2">
                                <div className="text-[12px]">
                                    <span className="text-[10px] font-extrabold uppercase tracking-wider text-amber-700 mr-2">Packing day</span>
                                    {packDate
                                        ? <span className={`font-bold ${current?.pack_tentative ? 'text-amber-800' : 'text-slate-800'}`}>{fmtMoveDate(packDate, { tentative: !!current?.pack_tentative, year: true })}{packTime ? <span className="font-normal text-slate-500"> · {packTime}</span> : null}</span>
                                        : <span className="text-slate-500">not set{moveDate ? ` (day before the move is ${fmtMoveDate(shiftDateStr(moveDate, -1))})` : ''}</span>}
                                </div>
                                <span className="text-[10px] text-slate-400">Edit on the Client Details tab</span>
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <FormField label="Crew Size">
                                    <ChoiceKeys items={PACKING_CREW_ITEMS} value={packingCrewSize} onChange={v => setPackingCrewSize(parseInt(v))} ariaLabel="Packing crew size" />
                                </FormField>
                                <FormField label="Rate ($/hr)">
                                    <NumberField step={5} prefix="$" inputClassName="font-semibold" value={packingRate || ''} onChange={v => setPackingRate(v)} placeholder="0" />
                                </FormField>
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <FormField label="Min Hours">
                                    <NumberField step={0.5} value={packingMinHours} onChange={v => setPackingMinHours(v)} placeholder="e.g. 4" />
                                </FormField>
                                <FormField label="Max Hours">
                                    <NumberField step={0.5} value={packingMaxHours} onChange={v => setPackingMaxHours(v)} placeholder="e.g. 8" />
                                </FormField>
                            </div>

                            {/* ── Extra packing days (Jensen 2026-08-25) ──────
                                Each becomes its own PACK phase → its own pill
                                on the dispatch board with its own date, start
                                time, crew, and hours estimate. */}
                            {extraPackDays.map((day, idx) => {
                                const dayMin = (parseFloat(day.minHours) || 0) * (parseFloat(day.rate) || 0);
                                const dayMax = (parseFloat(day.maxHours) || parseFloat(day.minHours) || 0) * (parseFloat(day.rate) || 0);
                                return (
                                    <div key={idx} className="border-t-2 border-amber-200/70 pt-3 space-y-3">
                                        <div className="flex items-center justify-between">
                                            <div className="text-[10px] font-black text-amber-600 uppercase tracking-wider">Packing Day {idx + 2}</div>
                                            <Button variant="danger" size="sm" onClick={() => removePackDay(idx)}>
                                                Remove day
                                            </Button>
                                        </div>
                                        <div className="grid grid-cols-2 gap-3">
                                            <FormField label="Packing Day">
                                                <DatePicker className={inputCls + ' border-amber-200 cursor-pointer'} value={day.date || ''} onChange={v => updatePackDay(idx, 'date', v)} placeholder="Pick a day..." />
                                            </FormField>
                                            <FormField label="Start Time">
                                                <TimePicker value={day.time || ''} onChange={v => updatePackDay(idx, 'time', v)} ariaLabel={`Packing day ${idx + 2} time`} />
                                            </FormField>
                                        </div>
                                        <div className="grid grid-cols-2 gap-3">
                                            <FormField label="Crew Size">
                                                <ChoiceKeys items={PACKING_CREW_ITEMS} value={day.crew} onChange={v => updatePackDay(idx, 'crew', parseInt(v))} ariaLabel={`Packing day ${idx + 2} crew size`} />
                                            </FormField>
                                            <FormField label="Rate ($/hr)">
                                                <NumberField step={5} prefix="$" inputClassName="font-semibold" value={day.rate || ''} onChange={v => updatePackDay(idx, 'rate', v)} placeholder="0" />
                                            </FormField>
                                        </div>
                                        <div className="grid grid-cols-2 gap-3">
                                            <FormField label="Min Hours">
                                                <NumberField step={0.5} value={day.minHours} onChange={v => updatePackDay(idx, 'minHours', v)} placeholder="e.g. 4" />
                                            </FormField>
                                            <FormField label="Max Hours">
                                                <NumberField step={0.5} value={day.maxHours} onChange={v => updatePackDay(idx, 'maxHours', v)} placeholder="e.g. 8" />
                                            </FormField>
                                        </div>
                                        {dayMin > 0 && (
                                            <div className="text-[11px] text-amber-600 font-semibold text-right">
                                                Day {idx + 2}: {dayMin !== dayMax && dayMax > 0 ? `$${dayMin.toLocaleString()} – $${dayMax.toLocaleString()}` : `$${dayMin.toLocaleString()}`} ({day.minHours}{day.maxHours && day.maxHours !== day.minHours ? `–${day.maxHours}` : ''} hrs × ${day.rate}/hr)
                                            </div>
                                        )}
                                    </div>
                                );
                            })}

                            <Button variant="tertiary" size="sm" full onClick={addPackDay}>
                                + Add Packing Day {extraPackDays.length + 2}
                            </Button>

                            {packingMinTotal > 0 && (
                                <div className="text-xs text-amber-700 font-semibold text-right">
                                    Packing{extraPackDays.length > 0 ? ` (${extraPackDays.length + 1} days)` : ''}: {packingMinTotal !== packingMaxTotal && packingMaxTotal > 0
                                        ? `$${packingMinTotal.toLocaleString()} – $${packingMaxTotal.toLocaleString()}`
                                        : `$${packingMinTotal.toLocaleString()}`
                                    }{extraPackDays.length === 0 && ` (${packingMinHours}${packingMaxHours && packingMaxHours !== packingMinHours ? `–${packingMaxHours}` : ''} hrs × $${packingRate}/hr)`}
                                </div>
                            )}
                        </div>
                    )}
                </div>
            )}

            {/* ===== STORAGE (sql/084-086) =====
                A storage job is two estimates. THIS estimate can be the
                move INTO storage (creates a QUOTED tenancy — flips to In
                Storage automatically when the move completes) or the move
                OUT of a unit the client already has. BBM units come from
                the inventory on Settings → Storage. */}
            {canEditPricing && !isEdit && (
                <div>
                    <h3 className="text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-2">Storage</h3>
                    <div className="flex gap-2 flex-wrap">
                        {[
                            { key: 'none', label: 'No storage' },
                            { key: 'into', label: '→ Into storage' },
                            { key: 'out', label: '← Out of storage', disabled: clientStorageTenancies.length === 0 },
                        ].map(opt => (
                            <button key={opt.key} type="button" disabled={opt.disabled}
                                onClick={() => setStorageMode(opt.key)}
                                title={opt.disabled ? 'No storage on file for this client yet' : undefined}
                                className={`px-3 py-2 rounded-lg border-2 text-sm font-bold transition-all disabled:opacity-40 ${storageMode === opt.key
                                    ? 'bg-indigo-50 border-indigo-400 text-indigo-700'
                                    : 'bg-white border-slate-200 text-slate-500 hover:border-slate-300'}`}>
                                {opt.label}
                            </button>
                        ))}
                    </div>

                    {storageMode === 'into' && (
                        <div className="mt-3 bg-indigo-50/60 border border-indigo-200 rounded-xl p-4 space-y-3">
                            <FormField label="Whose unit will it be?">
                                <div className="flex gap-2">
                                    <button type="button" onClick={() => setStorageOwnership('bbm')}
                                        className={`flex-1 px-3 py-2 rounded-lg border-2 text-sm font-bold transition-all ${storageOwnership === 'bbm' ? 'bg-red-50 border-red-400 text-red-700' : 'bg-white border-slate-200 text-slate-500 hover:border-slate-300'}`}>
                                        BBM unit <span className="block text-[10px] font-normal">one of ours — pick from the inventory</span>
                                    </button>
                                    <button type="button" onClick={() => setStorageOwnership('client')}
                                        className={`flex-1 px-3 py-2 rounded-lg border-2 text-sm font-bold transition-all ${storageOwnership === 'client' ? 'bg-slate-100 border-slate-400 text-slate-700' : 'bg-white border-slate-200 text-slate-500 hover:border-slate-300'}`}>
                                        Client's own <span className="block text-[10px] font-normal">their unit — we just track it</span>
                                    </button>
                                </div>
                            </FormField>

                            {storageOwnership === 'bbm' ? (
                                activeFacilities.length === 0 ? (
                                    <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">No storage facility set up yet — add one on <b>Settings → Storage</b>. You can still create this estimate; assign the unit later from the Storage page.</p>
                                ) : (
                                    <>
                                        {activeFacilities.length > 1 && (
                                            <FormField label="Facility">
                                                <Combobox
                                                    items={activeFacilities.map(f => ({ value: f.id, label: `${f.name}${f.is_default ? ' (default)' : ''}`, sub: f.address || undefined }))}
                                                    value={storageFacilityId || ''}
                                                    onChange={v => { setStorageFacilityId(v); setStoragePickedUnitIds([]); }}
                                                    clearable={false}
                                                    placeholder="Pick a facility…"
                                                    ariaLabel="Storage facility" />
                                            </FormField>
                                        )}
                                        <div>
                                            <div className="flex items-baseline justify-between gap-2 mb-1.5">
                                                <span className="text-xs font-bold text-slate-600">
                                                    {storageFacilityRow?.name}{storageFacilityRow?.address ? <span className="font-normal text-slate-400"> · {storageFacilityRow.address}</span> : null}
                                                </span>
                                                <span className="text-[10px] text-slate-400">tap one or more empty units · or leave blank to assign later</span>
                                            </div>
                                            {emptyUnitsBySize.length === 0 ? (
                                                <p className="text-xs font-semibold text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">No empty units at {storageFacilityRow?.name} right now. Create the estimate and assign a unit from the Storage page when one frees up.</p>
                                            ) : (
                                                <div className="space-y-2">
                                                    {emptyUnitsBySize.map(g => (
                                                        <div key={g.size} className="flex items-center gap-2 flex-wrap">
                                                            <span className="text-[11px] font-bold text-slate-500 w-20 shrink-0">{g.size}{storageFacilityRow?.size_rates?.[g.size] ? <span className="font-normal text-slate-400"> · ${storageFacilityRow.size_rates[g.size]}</span> : null}</span>
                                                            {g.units.map(u => {
                                                                const on = storagePickedUnitIds.includes(u.id);
                                                                return (
                                                                    <button key={u.id} type="button" onClick={() => togglePickedUnit(u.id)}
                                                                        title={u.notes || `Unit ${u.unit_number}`}
                                                                        className={`px-2.5 py-1 rounded-lg border-2 text-xs font-mono font-bold transition-all ${on ? 'bg-indigo-600 border-indigo-600 text-white shadow-sm' : 'bg-white border-emerald-200 text-slate-700 hover:border-indigo-300'}`}>
                                                                        {u.unit_number}
                                                                    </button>
                                                                );
                                                            })}
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                        {storagePickedUnitIds.length > 0 && (
                                            <div className="text-[11px] text-indigo-700 bg-white/70 border border-indigo-100 rounded-lg px-3 py-1.5 flex items-center gap-2 flex-wrap">
                                                <span>Reserving <b>{storagePickedUnitIds.map(id => storageUnitsById[id]?.unit_number).filter(Boolean).join(', ')}</b></span>
                                                {canViewPricing && pickedUnitsRate > 0 && <span className="text-slate-500">· rate card ${pickedUnitsRate}/mo — confirm what they'll be billed on the Storage page</span>}
                                                <Button variant="tertiary" size="sm" className="ml-auto" onClick={() => setStoragePickedUnitIds([])}>Clear</Button>
                                            </div>
                                        )}
                                    </>
                                )
                            ) : (
                                <div className="grid grid-cols-2 gap-3">
                                    <FormField label="Their facility (if known)">
                                        <input className={inputCls + ' border-indigo-200'} value={storageFacility} onChange={e => setStorageFacility(e.target.value)} placeholder="Name or address — TBD is fine" />
                                    </FormField>
                                    <FormField label="Unit # (if known)">
                                        <input className={inputCls + ' border-indigo-200'} value={storageUnitNumber} onChange={e => setStorageUnitNumber(e.target.value)} placeholder="TBD" />
                                    </FormField>
                                </div>
                            )}
                            <p className="text-[10px] text-indigo-500">
                                Creating this estimate adds a <b>Quoted</b> storage record{intoDestPreview ? <> and sets the destination to <b>{intoDestPreview}</b></> : null}. It flips to <b>In Storage</b> automatically when this move completes — billing, paid-thru, and fullness are managed on the Storage page.
                            </p>
                        </div>
                    )}

                    {storageMode === 'out' && (
                        <div className="mt-3 bg-indigo-50/60 border border-indigo-200 rounded-xl p-4 space-y-2">
                            <FormField label="Moving out of which unit?">
                                <div className="space-y-1.5">
                                    {clientStorageTenancies.map(t => (
                                        <button key={t.id} type="button" onClick={() => setStorageOutTenancyId(t.id)}
                                            className={`w-full text-left px-3 py-2 rounded-lg border-2 text-sm transition-all ${storageOutTenancyId === t.id ? 'bg-white border-indigo-400' : 'bg-white/60 border-slate-200 hover:border-slate-300'}`}>
                                            <span className="font-bold text-slate-800">{tenancyPlace(t, storageUnitsById, storageFacilitiesById)}</span>
                                            <span className="text-[11px] text-slate-500 ml-2">
                                                {t.ownership === 'bbm' ? 'BBM unit' : "client's own"} · {t.status === 'quoted' ? 'quoted' : 'in storage'}{t.expected_out ? ` · out: ${t.expected_out}` : ''}
                                            </span>
                                        </button>
                                    ))}
                                </div>
                            </FormField>
                            {storageOutTenancyId && <p className="text-[10px] text-indigo-500">The unit becomes this estimate's <b>origin</b>, and this estimate is linked as the move-out. Close the unit from the Storage page once they're fully out.</p>}
                        </div>
                    )}
                </div>
            )}

            {/* ===== ADDITIONAL FEES ===== */}
            {canEditPricing && (
                <div>
                    <h3 className="text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-3">Additional Fees</h3>
                    <div className="grid grid-cols-2 gap-3">
                        <div className="bg-rose-50 border border-rose-200 rounded-xl p-3">
                            <div className="flex items-center justify-between mb-2">
                                <span className="text-xs font-bold text-rose-700">Heavy Items</span>
                                <span className="text-[10px] text-rose-400">${heavyItemFee} each</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <NumberField step={1} className="w-32" inputClassName="text-lg font-bold text-rose-800" value={heavyItemCount} onChange={v => setHeavyItemCount(parseInt(v) || 0)} placeholder="0" ariaLabel="Heavy item count" />
                                {heavyTotal > 0 && <span className="text-xs font-semibold text-rose-600 ml-2">${heavyTotal}</span>}
                            </div>
                        </div>
                        <div className="bg-slate-50 border border-slate-200 rounded-xl p-3">
                            <span className="text-xs font-bold text-slate-600 block mb-2">Removal Fee</span>
                            <NumberField step={5} prefix="$" value={removalFee || ''} onChange={v => setRemovalFee(v)} placeholder="0" ariaLabel="Removal fee" />
                            <p className="text-[10px] text-slate-400 mt-1">Boxes, debris removal, etc.</p>
                        </div>
                    </div>
                </div>
            )}

            {/* ===== PHASE PLANNER =====
                Auto-seeded from job type + packing + crew inputs above.
                Each phase will land in the dispatch board as its own pill
                once this estimate is marked 'booked'. Dateless phases sit
                in the "unscheduled chunks" rail until a dispatcher drags
                them onto a day. */}
            {canEditPricing && (
                <div>
                    <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
                        <h3 className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">Dispatch Chunks</h3>
                        <div className="flex items-center gap-1">
                            {/* Planner pre-fill from the MOVE's dates (rule 8,
                                2026-09-18): PACK → LOAD → DRIVE → HOLD → UNLOAD
                                → RETURN from pack / load / unload. Offered
                                only while the planner is still the bare
                                auto-seed; never applied on its own. */}
                            {canPrefillFromMove && (
                                <Button variant="tertiary" size="sm" onClick={handlePrefillFromMove} title="Build the phases from the move's pack / load / unload dates">
                                    Pre-fill from move dates
                                </Button>
                            )}
                            {phasesDirty && (
                                <Button variant="tertiary" size="sm" onClick={handleResetPhases}>
                                    Reset to auto
                                </Button>
                            )}
                        </div>
                    </div>
                    <PhasePlannerInline
                        phases={phases}
                        onChange={handlePhasesChange}
                        moveDate={moveDate}
                    />
                </div>
            )}

            {/* ===== TOTAL ===== */}
            {canViewPricing && (totalMin > 0 || totalMax > 0) && (
                <div className="bg-slate-800 text-white rounded-xl p-5">
                    <div className="flex items-center justify-between">
                        <span className="text-sm font-bold uppercase tracking-wide">Estimate Total</span>
                        <span className="text-3xl font-extrabold">
                            {isRange
                                ? `$${totalMin.toLocaleString()} – $${totalMax.toLocaleString()}`
                                : `$${totalMin.toLocaleString()}`
                            }
                        </span>
                    </div>
                    <div className="mt-3 space-y-1 text-[11px] text-slate-400">
                        {jobType === 'LOCAL' && (minHours || maxHours) && (
                            <div>Labor: {minHours}{maxHours && maxHours !== minHours ? `–${maxHours}` : ''} hrs × ${effectiveRate}/hr = ${laborMin.toLocaleString()}{laborMin !== laborMax ? ` – $${laborMax.toLocaleString()}` : ''}</div>
                        )}
                        {jobType === 'LONG_DISTANCE' && bolPrice && <div>BOL Move: ${parseFloat(bolPrice).toLocaleString()}</div>}
                        {jobType === 'LONG_DISTANCE' && bolDepositNum > 0 && <div className="text-emerald-400">BOL Deposit: −${bolDepositNum.toLocaleString()}{bolDepositSuffix}</div>}
                        {packingMinTotal > 0 && <div>Packing: {packingMinHours}{packingMaxHours && packingMaxHours !== packingMinHours ? `–${packingMaxHours}` : ''} hrs × ${packingRate}/hr = ${packingMinTotal.toLocaleString()}{packingMinTotal !== packingMaxTotal && packingMaxTotal > 0 ? ` – $${packingMaxTotal.toLocaleString()}` : ''}</div>}
                        {/* Travel/materials are LOCAL-only — the OOS total is
                            tariff-based (bol + packing + heavy + removal − deposit)
                            and never includes them, so listing them under an OOS
                            total made the lines sum past it (QA M2 BUG-4). */}
                        {jobType === 'LOCAL' && travelFee > 0 && <div>Travel: ${parseFloat(travelFee).toLocaleString()}</div>}
                        {jobType === 'LOCAL' && holdFee > 0 && <div>Hold: ${parseFloat(holdFee).toLocaleString()}</div>}
                        {jobType === 'LOCAL' && materialsFee > 0 && <div>Materials: ${parseFloat(materialsFee).toLocaleString()}</div>}
                        {heavyTotal > 0 && <div>Heavy items: {heavyItemCount} × ${heavyItemFee} = ${heavyTotal.toLocaleString()}</div>}
                        {removalFee > 0 && <div>Removal: ${parseFloat(removalFee).toLocaleString()}</div>}
                    </div>
                </div>
            )}

            {/* ===== FURNITURE CLASS + DRAFT (sql/048 + sql/047) =====
                Furniture class lives on the client and informs crew/care
                allocation — set here at estimate-creation time and locked
                from edit elsewhere once an estimate exists. Draft toggle
                is the rare opt-out for keeping the estimate OUT of the
                callback queue. Priority is set independently from any
                admin surface (Clients page, Estimates page) — not here. */}
            {canEditPricing && !isEdit && (
                <div className="bg-white rounded-xl border border-slate-200 p-4 space-y-3">
                    <div className="flex items-center gap-2">
                        <h3 className="text-[11px] font-bold text-slate-500 uppercase tracking-wider">Furniture Class</h3>
                    </div>
                    <div>
                        <div className="grid grid-cols-4 gap-1.5">
                            {FURNITURE_CLASS_OPTIONS.map(opt => (
                                <button
                                    key={opt.key}
                                    type="button"
                                    onClick={() => updateFurnitureClass(opt.key)}
                                    className={`px-2 py-2 rounded-lg text-[11px] font-bold uppercase tracking-wide border-2 transition-all ${
                                        furnitureClass === opt.key
                                            ? 'text-white shadow-sm'
                                            : 'border-slate-200 bg-white hover:border-slate-300'
                                    }`}
                                    style={furnitureClass === opt.key
                                        ? { backgroundColor: opt.color, borderColor: opt.color }
                                        : { color: opt.color }}>
                                    {opt.label}
                                </button>
                            ))}
                        </div>
                    </div>
                    {/* Description of the currently-selected furniture tier. */}
                    <div className="text-[11px] text-slate-500 italic px-1">
                        <span className="font-semibold text-slate-700">{FURNITURE_CLASS_OPTIONS.find(f => f.key === furnitureClass)?.label}:</span>{' '}
                        {FURNITURE_SUBTITLES[furnitureClass]}
                    </div>
                    {/* Draft escape hatch — rare opt-out for keeping the estimate
                        OUT of the callback queue. Default off. */}
                    <div className="pt-1 border-t border-slate-100">
                        <Switch checked={!!isDraft} onChange={b => setIsDraft(b)} label="Save as draft (don't add to callback queue yet)" />
                    </div>
                </div>
            )}

            {/* ===== CREATE BUTTON ===== */}
            {canEditPricing && !isEdit && (
                <div className={`rounded-xl p-5 border ${moveDate ? 'bg-emerald-50 border-emerald-200' : 'bg-amber-50 border-amber-300'}`}>
                    {!moveDate && (
                        <div className="mb-3 flex items-start gap-2 p-2.5 bg-amber-100 border border-amber-300 rounded-lg">
                            <span className="text-amber-700 font-bold text-xs">⚠️ No move date set</span>
                            <span className="text-[11px] text-amber-700">Set a move date in the Client Details tab so this estimate can be scheduled on the dispatch board.</span>
                        </div>
                    )}
                    {/* Deposit carry-over — shows whenever a prior estimate
                        holds a paid deposit (super-admin+). The classic
                        case: destination changed after the deposit came in;
                        new option gets built, money + signature follow. */}
                    {depositSource && canTransferDeposits && (
                        <button
                            type="button"
                            role="checkbox"
                            aria-checked={transferDeposit}
                            onClick={() => setTransferDeposit(v => !v)}
                            className={`mb-3 w-full flex items-center gap-3 p-3 rounded-lg border text-left transition-colors ${transferDeposit ? 'bg-emerald-50 border-emerald-300' : 'bg-white border-slate-200 hover:border-slate-300'}`}
                        >
                            <span className={`w-5 h-5 rounded-md border-2 flex items-center justify-center flex-shrink-0 transition-colors ${transferDeposit ? 'bg-emerald-600 border-emerald-600' : 'bg-white border-slate-300'}`}>
                                {transferDeposit && (
                                    <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><polyline points="20 6 9 17 4 12" /></svg>
                                )}
                            </span>
                            <span>
                                <span className="block text-xs font-bold text-slate-700">
                                    Transfer paid deposit{depositSource.deposit_amount ? ` ($${Number(depositSource.deposit_amount).toLocaleString()})` : ''} + signed terms
                                </span>
                                <span className="block text-[11px] text-slate-500">
                                    From {depositSource.job_number} — it keeps a note of where they went.
                                </span>
                            </span>
                        </button>
                    )}
                    <div className="flex items-center justify-between">
                        <div>
                            <h4 className={`text-sm font-bold ${moveDate ? 'text-emerald-800' : 'text-amber-800'}`}>Ready to create estimate?</h4>
                            <p className={`text-xs mt-0.5 ${moveDate ? 'text-emerald-600' : 'text-amber-600'}`}>
                                {isDraft
                                    ? 'Will be saved as a draft (not added to callback queue).'
                                    : 'Will be added to the office callback queue.'}
                            </p>
                        </div>
                        {/* OOS estimates are creatable with a $0 total — the
                            intended flow (Jaylen 2026-06-09) is crew/truck +
                            estimated weight at review time, tariff price added
                            later while the estimate sits in Building. LOCAL
                            still requires a non-zero total (hours × rate
                            should always produce one). */}
                        <Button onClick={handleCreateClick} loading={creating} disabled={jobType !== 'LONG_DISTANCE' && totalMax === 0 && totalMin === 0}
                            leftIcon={<IconFileText size={16}/>}>
                            Create Estimate
                        </Button>
                    </div>
                </div>
            )}

            {!canViewPricing && (
                <div className="bg-slate-50 border border-slate-200 rounded-xl p-5 text-center">
                    <p className="text-sm text-slate-400">You don't have permission to see pricing details.</p>
                </div>
            )}

            {/* ===== PREVIOUS ESTIMATES ===== */}
            {!isEdit && estimates.length > 0 && (
                <div>
                    <h4 className="text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-1">Estimates &amp; move dates ({estimates.length})</h4>
                    <p className="text-[10px] text-slate-400 mb-2">Each job on file with its current move date — so a second move a week out reads clearly.</p>
                    <div className="space-y-1.5">
                        {estimates.map(est => {
                            const status = getEstimateStatus(est.status);
                            const estDate = est.move_date
                                ? (() => { try { return new Date(est.move_date + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }); } catch { return est.move_date; } })()
                                : null;
                            return (
                                <div key={est.id} className="flex items-center justify-between p-2.5 bg-slate-50 rounded-lg border border-slate-200">
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <span className="px-2 py-0.5 rounded text-[10px] font-bold" style={{ background: statusTint(status.color, status.bg), color: status.color }}>{status.label}</span>
                                        <span className="font-mono text-xs text-slate-500 font-bold">{est.job_number}</span>
                                        <span className="text-xs text-slate-400">{est.job_type === 'LONG_DISTANCE' ? 'OOS/BOL' : 'Local'}</span>
                                        <span className={`text-xs font-semibold ${estDate ? 'text-slate-700' : 'text-amber-600'}`}>📅 {estDate || 'no date'}</span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        {canViewPricing && est.total_min && est.total_min !== est.total_max ? (
                                            <span className="text-sm font-bold text-green-600">${Number(est.total_min).toLocaleString()} – ${Number(est.total_max).toLocaleString()}</span>
                                        ) : canViewPricing && (
                                            <span className="text-sm font-bold text-green-600">${Number(est.total_estimate || 0).toLocaleString()}</span>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            {confirmElement}
            <ConfirmDialog
                open={!!zeroCrewConfirm}
                title={`Create ${client?.name || 'this client'}'s OOS estimate with no crew?`}
                confirmLabel="Create anyway"
                cancelLabel="Go back"
                onConfirm={() => {
                    const pending = zeroCrewConfirm;
                    setZeroCrewConfirm(null);
                    handleCreateEstimate({ skipCrewCheck: true });
                }}
                onCancel={() => setZeroCrewConfirm(null)}>
                Load crew and travel crew are both 0 — every phase will land on the
                dispatch board with no crew preallocated. The estimate is still created
                and saved; you can set crew sizes on it later.
            </ConfirmDialog>
        </div>
    );

}
